/**
 * Earth tile layer — Day/night, clouds, and specular effects for MapLibre globe.
 *
 * A MapLibre CustomLayerInterface that composites visual effects on top of
 * the NASA GIBS Blue Marble + Black Marble raster tiles using multi-pass
 * WebGL rendering:
 *
 *   Capture layer — Snapshots the Black Marble tiles to an offscreen texture
 *   Pass 1 (multiply) — Darkens the night side of the Blue Marble tiles
 *   Pass 2 (additive) — Overlays Black Marble city lights (screen-space sampled)
 *   Pass 3 (additive) — Specular sun glint on water (masked by clouds)
 *   Pass 4 (alpha)    — Clouds with day/night darkening and zoom fade
 *
 * The capture layer renders between Black Marble and Blue Marble in the
 * MapLibre layer stack, copying the framebuffer (containing rendered Black
 * Marble tiles) to a texture. Pass 2 then samples this texture in screen
 * space, giving progressive tile-level zoom detail for night lights.
 *
 * All passes render a sphere matching MapLibre's ECEF globe geometry with
 * depth test disabled to avoid z-fighting with the tile layer.
 */

import type { CustomLayerInterface } from 'maplibre-gl'
import type { Map as MaplibreMap } from 'maplibre-gl'
import { getSunPosition } from '../utils/time'
import {
  getShaderSettings,
  onShaderSettingsChange,
} from './shaderSettingsService'
import { logger } from '../utils/logger'
import { getCloudTextureUrl, isMobile } from '../utils/deviceCapability'
import { reportError } from '../analytics'
import type { DatasetOverlayOptions } from '../types'
import { COLOR_SCALE_LUT_SIZE } from '../types/color-scale'
import {
  DEFAULT_DISPLAY,
  buildDisplayLut,
  type ColorScaleDisplay,
} from './colorScaleDisplay'
import {
  ATMOSPHERE_GLSL_CONSTANTS,
  ATMOSPHERE_GLSL_DENSITY,
  ATMOSPHERE_GLSL_PHASE,
  ATMOSPHERE_GLSL_INTERSECT,
  ATMOSPHERE_GLSL_TONEMAP,
  ATMOSPHERE_STEPS_HIGH,
  ATMOSPHERE_STEPS_MOBILE,
  ATMOSPHERE_RADIUS_FACTOR,
  PLANET_RADIUS_KM,
  buildAtmosphereRaymarchGlsl,
} from './atmosphereConstants'
import { computeTransmittanceLut } from './atmosphereLut'
import { EARTH_ASSET_BASE } from '../config/endpoints'

// Pick step counts once at module load. The shader source is a
// top-level constant in this file (compiled when the layer is
// first added to a map), so this matches the lifetime of the
// program. If the user resizes a desktop browser to phone width
// the shader doesn't re-compile — fine for this build's audience.
const ATMOSPHERE_STEPS = isMobile() ? ATMOSPHERE_STEPS_MOBILE : ATMOSPHERE_STEPS_HIGH

// --- Texture URLs ---
const SPECULAR_MAP_URL = '/assets/Earth_Specular_2K.jpg'
// §7.2 normal-map asset. Tangent-space encoding (R/G/B = X/Y/Z of
// the surface normal in [0,255]), equirectangular projection.
// Hosted on the same CloudFront + S3 path the VR diffuse / lights
// tiers use (see EARTH_TEXTURE_BASE in photorealEarth.ts) — same
// `metadata.sosexplorer.gov` bucket fronted by CloudFront.
//
// Tiers walked in ascending order, mirroring the existing
// `loadProgressive` ladder for the diffuse / night-lights maps:
// each successful tier replaces the bound texture (re-uploads via
// texImage2D into the same `bumpTex` handle, so no GPU object
// churn), regenerates mipmaps, and triggers a repaint. On a 404
// or network error, progression stops and the most recently
// loaded tier stays applied — so a fresh stack with only the 8K
// uploaded still works, and a bandwidth-constrained client that
// times out fetching 4K keeps the 2K bump it already has.
const NORMAL_MAP_URLS = [
  `${EARTH_ASSET_BASE}/earth_normal_2048.jpg`,
  `${EARTH_ASSET_BASE}/earth_normal_4096.jpg`,
  `${EARTH_ASSET_BASE}/earth_normal_8192.jpg`,
]
const CLOUD_TEXTURE_URL = getCloudTextureUrl()

// --- Rendering constants (matched to earthMaterials.ts) ---
const NIGHT_LIGHT_STRENGTH = 0.5
const NIGHT_DARKENING = 0.01 // how dark the night side gets (before city lights)
const CLOUD_RADIUS = 1.005   // slightly above globe surface
const CLOUD_OPACITY = 0.65
const CLOUD_ALPHA_GAMMA = 1.8
const CLOUD_NIGHT_DARKENING = 0.08
const SPECULAR_SHININESS = 40.0
// Specular strength moved to shaderSettingsService (§7.2) — the
// shipped default lives in SHADER_DEFAULTS there, and the live
// value flows through Tools → Display preset clicks + the
// `?tune=shader` slider page. Pre-§7.2 value was 0.60 (kept as
// the Tools-menu "Comfortable" preset, slightly reduced to 0.55
// per Adrian's "reduce specular" ask).
const STAR_BRIGHTNESS = 0.2
/**
 * Multiplier on the raymarched scattering colour before additive
 * blend. The raymarch produces HDR values that ACES compresses to
 * [0,1]; this knob lets us dial down the limb glow without changing
 * the physical model. 1.0 = full strength.
 */
const ATMOSPHERE_INTENSITY = 1.0
const SKYBOX_FACES = ['px', 'nx', 'py', 'ny', 'pz', 'nz'] as const
const SKYBOX_URL_BASE = '/assets/skybox/'

// --- Texture utility ---

/**
 * Returns either the original source or a canvas-downscaled copy that fits
 * within `gl.MAX_TEXTURE_SIZE`. macOS Firefox caps this at 8192, while the
 * cloud asset is 10000×5000 — without this clamp the upload silently fails
 * (INVALID_VALUE) and the texture stays as the 1×1 placeholder. Dataset
 * image fallbacks may also exceed the cap when the `_4096`/`_2048` variants
 * 404 and we fetch the upstream original.
 */
function fitImageToMaxTextureSize(
  gl: WebGL2RenderingContext,
  source: HTMLImageElement | HTMLCanvasElement,
): HTMLImageElement | HTMLCanvasElement {
  const max = gl.getParameter(gl.MAX_TEXTURE_SIZE) as number
  if (source.width <= max && source.height <= max) return source
  const scale = Math.min(max / source.width, max / source.height)
  const w = Math.floor(source.width * scale)
  const h = Math.floor(source.height * scale)
  const canvas = document.createElement('canvas')
  canvas.width = w
  canvas.height = h
  const ctx = canvas.getContext('2d')
  if (!ctx) return source
  ctx.drawImage(source, 0, 0, w, h)
  logger.warn('[EarthTileLayer] Texture %dx%d exceeds MAX_TEXTURE_SIZE %d — downscaled to %dx%d',
    source.width, source.height, max, w, h)
  return canvas
}

// --- Matrix utility ---

/** Invert a 4x4 column-major matrix. Returns false if singular. */
function invertMat4(out: Float32Array, m: ArrayLike<number>): boolean {
  const a00=m[0],a01=m[1],a02=m[2],a03=m[3],a10=m[4],a11=m[5],a12=m[6],a13=m[7]
  const a20=m[8],a21=m[9],a22=m[10],a23=m[11],a30=m[12],a31=m[13],a32=m[14],a33=m[15]
  const b00=a00*a11-a01*a10,b01=a00*a12-a02*a10,b02=a00*a13-a03*a10
  const b03=a01*a12-a02*a11,b04=a01*a13-a03*a11,b05=a02*a13-a03*a12
  const b06=a20*a31-a21*a30,b07=a20*a32-a22*a30,b08=a20*a33-a23*a30
  const b09=a21*a32-a22*a31,b10=a21*a33-a23*a31,b11=a22*a33-a23*a32
  let det=b00*b11-b01*b10+b02*b09+b03*b08-b04*b07+b05*b06
  if (Math.abs(det)<1e-10) return false
  det=1/det
  out[0]=(a11*b11-a12*b10+a13*b09)*det;out[1]=(a02*b10-a01*b11-a03*b09)*det
  out[2]=(a31*b05-a32*b04+a33*b03)*det;out[3]=(a22*b04-a21*b05-a23*b03)*det
  out[4]=(a12*b08-a10*b11-a13*b07)*det;out[5]=(a00*b11-a02*b08+a03*b07)*det
  out[6]=(a32*b02-a30*b05-a33*b01)*det;out[7]=(a20*b05-a22*b02+a23*b01)*det
  out[8]=(a10*b10-a11*b08+a13*b06)*det;out[9]=(a01*b08-a00*b10-a03*b06)*det
  out[10]=(a30*b04-a31*b02+a33*b00)*det;out[11]=(a21*b02-a20*b04-a23*b00)*det
  out[12]=(a11*b07-a10*b09-a12*b06)*det;out[13]=(a00*b09-a01*b07+a02*b06)*det
  out[14]=(a31*b01-a30*b03-a32*b00)*det;out[15]=(a20*b03-a21*b01+a22*b00)*det
  return true
}

// --- Camera position extraction ---
//
// MapLibre exposes the projection-view matrix (`uMatrix`) but no
// direct camera position. For Tier-2 raymarching we need the camera
// position in the same ECEF unit-sphere coordinate frame as the
// rendered globe so the ray geometry is correct at any zoom level.
//
// Method: two view rays through clip space (one centred, one
// off-axis) meet at the camera for a perspective projection. We
// invert `uMatrix`, project two pairs of clip-space points through
// it to get four world-space points, then find the closest point on
// the two world-space lines — that point is the camera.

const _invMatrixScratch = new Float32Array(16)
const _projScratch1 = new Float32Array(3)
const _projScratch2 = new Float32Array(3)
const _projScratch3 = new Float32Array(3)
const _projScratch4 = new Float32Array(3)

function unprojectClip(
  invM: Float32Array, x: number, y: number, z: number, out: Float32Array,
): boolean {
  const w = invM[3] * x + invM[7] * y + invM[11] * z + invM[15]
  // Near-zero w means the clip-space point sits on the projective
  // infinity plane (camera origin under perspective). Bail rather
  // than produce ±Inf / NaN that would propagate into uniforms.
  if (Math.abs(w) < 1e-10) return false
  const iw = 1 / w
  out[0] = (invM[0] * x + invM[4] * y + invM[8] * z + invM[12]) * iw
  out[1] = (invM[1] * x + invM[5] * y + invM[9] * z + invM[13]) * iw
  out[2] = (invM[2] * x + invM[6] * y + invM[10] * z + invM[14]) * iw
  return true
}

/**
 * Camera position in the world-space coord frame `matrix` projects
 * from. For MapLibre's globe view that's ECEF unit-sphere coords
 * (planet radius = 1). Caller scales to km if needed.
 *
 * Writes into `out` and returns `true` on success. Returns `false`
 * (without touching `out`) if the matrix is singular, any
 * unprojection lands on the projective-infinity plane, or the two
 * world-space rays are parallel — caller must skip the atmosphere
 * pass in that case. Falling back to (0, 0, 0) would put the
 * raymarch's ray origin at the planet centre, which produces
 * extreme artifacts rather than a clean degradation.
 */
function computeCameraWorldPosFromMatrix(
  matrix: ArrayLike<number>, out: [number, number, number],
): boolean {
  if (!invertMat4(_invMatrixScratch, matrix)) return false
  if (
    !unprojectClip(_invMatrixScratch, 0,   0, -1, _projScratch1) ||
    !unprojectClip(_invMatrixScratch, 0,   0,  1, _projScratch2) ||
    !unprojectClip(_invMatrixScratch, 0.5, 0, -1, _projScratch3) ||
    !unprojectClip(_invMatrixScratch, 0.5, 0,  1, _projScratch4)
  ) {
    return false
  }

  const d1x = _projScratch2[0] - _projScratch1[0]
  const d1y = _projScratch2[1] - _projScratch1[1]
  const d1z = _projScratch2[2] - _projScratch1[2]
  const d2x = _projScratch4[0] - _projScratch3[0]
  const d2y = _projScratch4[1] - _projScratch3[1]
  const d2z = _projScratch4[2] - _projScratch3[2]
  const ex = _projScratch3[0] - _projScratch1[0]
  const ey = _projScratch3[1] - _projScratch1[1]
  const ez = _projScratch3[2] - _projScratch1[2]

  const d1d1 = d1x * d1x + d1y * d1y + d1z * d1z
  const d1d2 = d1x * d2x + d1y * d2y + d1z * d2z
  const d2d2 = d2x * d2x + d2y * d2y + d2z * d2z
  const ed1 = ex * d1x + ey * d1y + ez * d1z
  const ed2 = ex * d2x + ey * d2y + ez * d2z
  const denom = d1d1 * d2d2 - d1d2 * d1d2

  // Parallel rays (orthographic projection) — no perspective
  // convergence point, no camera position to extract.
  if (Math.abs(denom) < 1e-10) return false

  const t = (ed1 * d2d2 - ed2 * d1d2) / denom
  out[0] = _projScratch1[0] + t * d1x
  out[1] = _projScratch1[1] + t * d1y
  out[2] = _projScratch1[2] + t * d1z
  return true
}

// --- Sphere geometry ---

interface SphereBuffers {
  positions: Float32Array
  normals: Float32Array
  uvs: Float32Array
  indices: Uint16Array
  indexCount: number
}

/**
 * Generate a UV sphere with equirectangular texture coordinates.
 * Coordinate convention matches MapLibre's ECEF globe:
 *   x = cos(lat) * sin(lng)
 *   y = sin(lat)
 *   z = cos(lat) * cos(lng)
 * where lat ∈ [+π/2, -π/2] (north→south), lng ∈ [-π, +π].
 *
 * UV mapping: u=0 at -180°, u=0.5 at 0° (prime meridian), u=1 at +180°.
 */
function createSphereGeometry(radius: number, wSegs: number, hSegs: number): SphereBuffers {
  const positions: number[] = []
  const normals: number[] = []
  const uvs: number[] = []
  const indices: number[] = []

  for (let y = 0; y <= hSegs; y++) {
    const v = y / hSegs
    const lat = Math.PI / 2 - v * Math.PI // +90° at top to -90° at bottom

    for (let x = 0; x <= wSegs; x++) {
      const u = x / wSegs
      const lng = u * 2 * Math.PI - Math.PI // -180° to +180°

      // MapLibre ECEF convention
      const nx = Math.cos(lat) * Math.sin(lng)
      const ny = Math.sin(lat)
      const nz = Math.cos(lat) * Math.cos(lng)

      positions.push(radius * nx, radius * ny, radius * nz)
      normals.push(nx, ny, nz)
      uvs.push(u, v)
    }
  }

  for (let y = 0; y < hSegs; y++) {
    for (let x = 0; x < wSegs; x++) {
      const a = y * (wSegs + 1) + x
      const b = a + wSegs + 1
      indices.push(a, b, a + 1)
      indices.push(b, b + 1, a + 1)
    }
  }

  return {
    positions: new Float32Array(positions),
    normals: new Float32Array(normals),
    uvs: new Float32Array(uvs),
    indices: new Uint16Array(indices),
    indexCount: indices.length,
  }
}

// --- Sun direction ---

/** Convert geographic lat/lng (degrees) to a unit direction vector (MapLibre ECEF convention). */
function sunDirectionFromLatLng(latDeg: number, lngDeg: number): [number, number, number] {
  const lat = latDeg * Math.PI / 180
  const lng = lngDeg * Math.PI / 180
  return [
    Math.cos(lat) * Math.sin(lng),
    Math.sin(lat),
    Math.cos(lat) * Math.cos(lng),
  ]
}

// --- Shader source ---

// Pass 0: replace blend — contrast / saturation post-process (§7.2).
//
// Runs FIRST in the earth-tile-layer's render pipeline, before the
// night-darkening / lights / specular / cloud / atmosphere passes.
// Captures the existing framebuffer (NASA Blue Marble tiles as drawn
// by MapLibre) to a screen-space texture, applies the contrast and
// saturation uniforms, and writes the corrected colour back with a
// `(ONE, ZERO)` blend — i.e. straight replace. Pass 1 onwards then
// composites night, lights, glint, clouds, and atmosphere on top of
// the corrected base. Sphere geometry is used so the colour
// correction only touches Earth pixels — the skybox / space
// background outside the sphere is untouched.
//
// Uniforms come from shaderSettingsService:
//   - uContrast    — pivot-around-0.5 contrast (1.0 == identity)
//   - uSaturation  — luminance-anchored saturation (1.0 == identity)
// Setting both to 1.0 (the universal identity) leaves the
// framebuffer byte-equivalent to the un-corrected baseline; the
// shipped non-1 values bring the look closer to Adrian's Blue Marble
// reference.
const colorCorrectVertSrc = `#version 300 es
  layout(location = 0) in vec3 aPosition;
  uniform mat4 uMatrix;
  uniform float uRadiusScale;

  void main() {
    gl_Position = uMatrix * vec4(aPosition * uRadiusScale, 1.0);
  }
`

const colorCorrectFragSrc = `#version 300 es
  precision highp float;
  uniform sampler2D uFramebuffer;
  uniform vec2 uViewportSize;
  uniform float uContrast;
  uniform float uSaturation;
  out vec4 fragColor;

  void main() {
    vec2 screenUV = gl_FragCoord.xy / uViewportSize;
    vec3 color = texture(uFramebuffer, screenUV).rgb;

    // Contrast — push values away from / toward 0.5 grey. Identity
    // at uContrast == 1.0.
    color = (color - 0.5) * uContrast + 0.5;

    // Saturation — mix between the luma (grayscale) and the original
    // colour. Identity at uSaturation == 1.0.
    // Rec. 709 luminance weights (same as the cloud-mask gating in
    // the specular pass).
    float luma = dot(color, vec3(0.299, 0.587, 0.114));
    color = mix(vec3(luma), color, uSaturation);

    fragColor = vec4(clamp(color, 0.0, 1.0), 1.0);
  }
`

// Pass 1.5: multiply blend — bump shading from a global normal map (§7.2).
//
// Runs after Pass 1 (night darken) but before Pass 2 (city lights),
// so the bumps shade the diffuse Earth without muddying the city-
// lights pass. Day-side only — gated by smoothstep on the un-
// perturbed NdotL so the night side stays uniform and lights read
// clean.
//
// The normal map is an equirectangular, tangent-space encoded image
// (R/G/B = X/Y/Z of the local-space normal, [0, 255]). We
// reconstruct the tangent-space normal, build a TBN matrix from the
// sphere surface normal at the fragment, transform into world
// space, and recompute NdotL with the perturbed normal. The output
// is the RATIO between the new and old NdotL — under multiply
// blend, a ratio >1 brightens the fragment (rough side toward sun)
// and <1 darkens it (turned away). Mixed back to 1.0 by
// uBumpStrength so the effect can be tuned via the ?tune=shader
// page; 0.0 == no bumps, 1.0 == full normal-map shading.
const bumpVertSrc = `#version 300 es
  layout(location = 0) in vec3 aPosition;
  layout(location = 1) in vec3 aNormal;
  layout(location = 2) in vec2 aUV;
  uniform mat4 uMatrix;
  uniform float uRadiusScale;
  out vec3 vNormal;
  out vec2 vUV;

  void main() {
    vNormal = aNormal;
    vUV = aUV;
    gl_Position = uMatrix * vec4(aPosition * uRadiusScale, 1.0);
  }
`

const bumpFragSrc = `#version 300 es
  precision highp float;
  uniform vec3 uSunDir;
  uniform sampler2D uNormalMap;
  uniform float uStrength;
  in vec3 vNormal;
  in vec2 vUV;
  out vec4 fragColor;

  void main() {
    vec3 N = normalize(vNormal);

    // Use the sphere geometry's UV directly — it's already
    // equirectangular with u=0 at the dateline and v=0 at the
    // north pole, matching how every other texture in this layer
    // (Blue Marble, specular, clouds) is sampled. A fragment-
    // computed vec2(lon, lat) UV would invert V (image y=0 vs
    // mathematical lat=+PI/2) and put the north-pole normals on
    // the south-pole pixels.
    vec2 uv = vUV;

    // Tangent-space normal from the map. Decode each channel from
    // [0, 1] to [-1, 1], then renormalise so JPEG round-trip and
    // mipmap-LOD bilinear blending don't leave us with sub-unit
    // (or super-unit) normals.
    vec3 tN = normalize(texture(uNormalMap, uv).rgb * 2.0 - 1.0);

    // Build TBN from the sphere surface normal. T points east, B
    // points north. At the poles, N is colinear with the world-up
    // axis used to derive T, so we fall back to a perpendicular
    // up-axis there. Without this guard the cross product
    // collapses to (0, 0, 0) and the normalize blows the entire
    // polar cap up into NaN-shaded garbage.
    vec3 up = abs(N.y) > 0.999 ? vec3(0.0, 0.0, 1.0) : vec3(0.0, 1.0, 0.0);
    vec3 T = normalize(cross(up, N));
    vec3 B = cross(N, T);
    mat3 TBN = mat3(T, B, N);

    vec3 perturbed = normalize(TBN * tN);

    float ndotL_flat = max(0.0, dot(N, uSunDir));
    float ndotL_pert = max(0.0, dot(perturbed, uSunDir));

    // Day-side mask — night side returns to ratio 1.0 (no change)
    // so the city-lights pass below sees clean diffuse.
    float dayMask = smoothstep(0.0, 0.2, ndotL_flat);

    // Ratio. Floor at 0.1 to avoid divide-by-near-zero near the
    // terminator (where ndotL_flat trends to 0). Clamp the result
    // so a single pixel can't push the framebuffer to extremes.
    float ratio = ndotL_pert / max(ndotL_flat, 0.1);
    float shading = mix(1.0, ratio, uStrength);
    shading = mix(1.0, shading, dayMask);
    shading = clamp(shading, 0.5, 1.5);

    fragColor = vec4(vec3(shading), 1.0);
  }
`

// Pass 1: multiply blend — darkens night side of existing tiles
const darkenVertSrc = `#version 300 es
  layout(location = 0) in vec3 aPosition;
  layout(location = 1) in vec3 aNormal;
  uniform mat4 uMatrix;
  uniform float uRadiusScale;
  out vec3 vNormal;

  void main() {
    vNormal = aNormal;
    gl_Position = uMatrix * vec4(aPosition * uRadiusScale, 1.0);
  }
`

const darkenFragSrc = `#version 300 es
  precision highp float;
  uniform vec3 uSunDir;
  in vec3 vNormal;
  out vec4 fragColor;

  void main() {
    vec3 N = normalize(vNormal);
    float NdotL = dot(N, uSunDir);

    // smoothstep matches earthMaterials.ts: smoothstep(0.0, -0.2, NdotL)
    float nightFactor = smoothstep(0.0, -0.2, NdotL);

    // Day side → white (1.0) = no change under multiply blend
    // Night side → dark (NIGHT_DARKENING) to simulate unlit Earth
    float brightness = mix(1.0, ${NIGHT_DARKENING.toFixed(4)}, nightFactor);

    fragColor = vec4(vec3(brightness), 1.0);
  }
`

// Pass 2: additive blend — overlays Black Marble city lights (screen-space sampled)
const lightsVertSrc = `#version 300 es
  layout(location = 0) in vec3 aPosition;
  layout(location = 1) in vec3 aNormal;
  uniform mat4 uMatrix;
  uniform float uRadiusScale;
  out vec3 vNormal;

  void main() {
    vNormal = aNormal;
    gl_Position = uMatrix * vec4(aPosition * uRadiusScale, 1.0);
  }
`

const lightsFragSrc = `#version 300 es
  precision highp float;
  uniform vec3 uSunDir;
  uniform sampler2D uBlackMarble;
  uniform vec2 uViewportSize;
  uniform float uLightStrength;
  in vec3 vNormal;
  out vec4 fragColor;

  void main() {
    vec3 N = normalize(vNormal);
    float NdotL = dot(N, uSunDir);

    // Night factor — city lights only visible on dark side
    float nightFactor = smoothstep(0.0, -0.2, NdotL);

    // Sample Black Marble tiles at screen position (captured from framebuffer)
    vec2 screenUV = gl_FragCoord.xy / uViewportSize;
    vec3 lights = texture(uBlackMarble, screenUV).rgb;

    // Scale by night factor and strength
    // Under additive blend: black (0,0,0) = no change on day side
    vec3 emission = lights * nightFactor * uLightStrength;

    fragColor = vec4(emission, 1.0);
  }
`

// Pass 3: alpha blend — clouds with day/night darkening
const cloudsVertSrc = `#version 300 es
  layout(location = 0) in vec3 aPosition;
  layout(location = 1) in vec3 aNormal;
  layout(location = 2) in vec2 aUV;
  uniform mat4 uMatrix;
  uniform float uRadius;
  uniform float uZoomFade;
  out vec3 vNormal;
  out vec2 vUV;
  out float vZoomFade;

  void main() {
    vNormal = aNormal;
    vUV = aUV;
    vZoomFade = uZoomFade;
    gl_Position = uMatrix * vec4(aPosition * uRadius, 1.0);
  }
`

const cloudsFragSrc = `#version 300 es
  precision highp float;
  uniform vec3 uSunDir;
  uniform sampler2D uCloudTex;
  uniform float uOpacity;
  uniform float uAlphaGamma;
  uniform float uNightDarkening;
  in vec3 vNormal;
  in vec2 vUV;
  in float vZoomFade;
  out vec4 fragColor;

  void main() {
    vec3 N = normalize(vNormal);
    float NdotL = dot(N, uSunDir);

    // Sample cloud texture and convert luminance to alpha
    vec4 texColor = texture(uCloudTex, vUV);
    float lum = dot(texColor.rgb, vec3(0.299, 0.587, 0.114));
    float cloudAlpha = pow(lum, uAlphaGamma) * uOpacity;

    // Day/night factor
    float nightMask = smoothstep(0.0, -0.2, NdotL);

    // Day side: white clouds with alpha transparency
    // Night side: black clouds that block city lights below
    // Boost night-side alpha so even thin clouds obscure lights
    vec3 color = mix(vec3(1.0), vec3(0.0), nightMask);
    float alpha = mix(cloudAlpha, min(cloudAlpha * 2.5, 1.0), nightMask);

    // Fade out clouds as camera zooms in
    alpha *= vZoomFade;

    fragColor = vec4(color, alpha);
  }
`

// Pass 4: additive blend — specular sun glint on water
const specularVertSrc = `#version 300 es
  layout(location = 0) in vec3 aPosition;
  layout(location = 1) in vec3 aNormal;
  layout(location = 2) in vec2 aUV;
  uniform mat4 uMatrix;
  uniform float uRadiusScale;
  out vec3 vNormal;
  out vec2 vUV;

  void main() {
    vNormal = aNormal;
    vUV = aUV;
    gl_Position = uMatrix * vec4(aPosition * uRadiusScale, 1.0);
  }
`

const specularFragSrc = `#version 300 es
  precision highp float;
  uniform vec3 uSunDir;
  uniform vec3 uViewDir;
  uniform sampler2D uSpecMap;
  uniform sampler2D uCloudMask;
  uniform float uShininess;
  uniform float uStrength;
  uniform float uCloudAlphaGamma;
  // 1.0 at zoom levels where the cloud pass renders clouds, fading
  // to 0 as the cloud pass fades. Without this the cloud mask
  // still suppresses sun glint at high zoom — clouds disappear
  // visually but their "shadow" stays on the water surface.
  uniform float uCloudZoomFade;
  in vec3 vNormal;
  in vec2 vUV;
  out vec4 fragColor;

  void main() {
    vec3 N = normalize(vNormal);
    vec3 L = normalize(uSunDir);
    vec3 V = normalize(uViewDir);

    // Blinn-Phong half vector
    vec3 H = normalize(L + V);
    float spec = pow(max(dot(N, H), 0.0), uShininess);

    // Only on the lit side
    float NdotL = dot(N, L);
    spec *= smoothstep(-0.1, 0.1, NdotL);

    // Mask by specular map (bright = water/shiny, dark = land/matte)
    float specMask = texture(uSpecMap, vUV).r;

    // Reduce glint under clouds — sample cloud density, gated by
    // the same zoom-fade the cloud pass uses so the mask matches
    // what's actually visible.
    vec4 cloudSample = texture(uCloudMask, vUV);
    float cloudLum = dot(cloudSample.rgb, vec3(0.299, 0.587, 0.114));
    float cloudDensity = pow(cloudLum, uCloudAlphaGamma) * uCloudZoomFade;
    spec *= specMask * uStrength * (1.0 - cloudDensity);

    // Additive: white highlight
    fragColor = vec4(vec3(spec), 1.0);
  }
`

// Pass 5: additive blend — atmospheric scattering halo around the globe.
// Tier-2 bounded raymarch matching `photorealEarth.ts` and the article's
// "rendering planets" case. The shell mesh sits at exactly the
// atmosphere boundary (`ATMOSPHERE_RADIUS_FACTOR ≈ 1.0157` from the
// shared constants) so every front-face fragment is the camera-side
// entry point for that pixel's ray, and the raymarch in the fragment
// shader integrates inward.
const atmosphereVertSrc = `#version 300 es
  layout(location = 0) in vec3 aPosition;
  uniform mat4 uMatrix;
  uniform float uRadiusScale;
  // Fragment world position in km (planet at origin, radius 6371).
  // aPosition * uRadiusScale is in ECEF unit-sphere units; scaling
  // to km lines up with the constants in the shared GLSL block so
  // density / extinction math is dimensionally clean.
  out vec3 vWorldPositionKm;

  void main() {
    vec3 unit = aPosition * uRadiusScale;
    vWorldPositionKm = unit * ${PLANET_RADIUS_KM.toFixed(1)};
    gl_Position = uMatrix * vec4(unit, 1.0);
  }
`

// GLSL 3.00 ES sampleTransmittanceLut helper. The shared raymarch
// declares the function; we define it ahead of the raymarch
// injection. The photorealEarth.ts (Three.js, GLSL 1.00) path
// defines an equivalent helper using texture2D() instead.
const atmosphereLutSamplerHelper = /* glsl */ `
  uniform sampler2D uTransmittanceLut;
  vec3 sampleTransmittanceLut(vec3 samplePos, vec3 sunDir) {
    float altitude = length(samplePos) - PLANET_RADIUS;
    float mu = dot(normalize(samplePos), sunDir);
    vec2 lutUV = vec2(
      mu * 0.5 + 0.5,
      clamp(altitude / ATMOSPHERE_HEIGHT, 0.0, 1.0)
    );
    return texture(uTransmittanceLut, lutUV).rgb;
  }
`

const atmosphereFragSrc = `#version 300 es
  precision highp float;
  uniform vec3 uSunDir;
  uniform vec3 uCameraPosKm;
  uniform float uIntensity;
  in vec3 vWorldPositionKm;
  out vec4 fragColor;
  ${ATMOSPHERE_GLSL_CONSTANTS}
  ${ATMOSPHERE_GLSL_DENSITY}
  ${ATMOSPHERE_GLSL_PHASE}
  ${ATMOSPHERE_GLSL_INTERSECT}
  ${ATMOSPHERE_GLSL_TONEMAP}
  ${atmosphereLutSamplerHelper}
  ${buildAtmosphereRaymarchGlsl(ATMOSPHERE_STEPS)}

  void main() {
    vec3 rayDir = normalize(vWorldPositionKm - uCameraPosKm);
    // result.rgb = HDR in-scattered light; result.a = view
    // transmittance scalar. Composition is article-style:
    // framebuffer becomes  scattered + bg × viewTrans
    // via blendFunc(ONE, SRC_ALPHA) in the render call.
    vec4 result = computeAtmosphereScattering(uCameraPosKm, rayDir, uSunDir);
    fragColor = vec4(acesFilm(result.rgb) * uIntensity, result.a);
  }
`

interface AtmosphereProgram {
  program: WebGLProgram
  matrixLoc: WebGLUniformLocation | null
  radiusScaleLoc: WebGLUniformLocation | null
  sunDirLoc: WebGLUniformLocation | null
  cameraPosKmLoc: WebGLUniformLocation | null
  intensityLoc: WebGLUniformLocation | null
  transmittanceLutLoc: WebGLUniformLocation | null
}

// Skybox: full-screen pass that samples cubemap faces based on camera rotation
const skyboxVertSrc = `#version 300 es
  out vec2 vScreenPos;
  void main() {
    float x = float((gl_VertexID & 1) << 2) - 1.0;
    float y = float((gl_VertexID & 2) << 1) - 1.0;
    vScreenPos = vec2(x, y);
    gl_Position = vec4(x, y, 1.0, 1.0);
  }
`

const skyboxFragSrc = `#version 300 es
  precision highp float;
  uniform sampler2D uFaces[6];
  uniform vec4 uCamera; // (lat, lng, bearing, pitch) in radians
  uniform vec2 uAspectFov; // (aspect ratio, fov in radians)
  uniform float uBrightness;
  in vec2 vScreenPos;
  out vec4 fragColor;

  // Rotation helpers
  vec3 rotX(float a, vec3 v) {
    float c = cos(a), s = sin(a);
    return vec3(v.x, v.y*c - v.z*s, v.y*s + v.z*c);
  }
  vec3 rotY(float a, vec3 v) {
    float c = cos(a), s = sin(a);
    return vec3(v.x*c + v.z*s, v.y, -v.x*s + v.z*c);
  }
  vec3 rotZ(float a, vec3 v) {
    float c = cos(a), s = sin(a);
    return vec3(v.x*c - v.y*s, v.x*s + v.y*c, v.z);
  }

  void main() {
    // Ray direction from screen position using real FOV and aspect ratio
    float halfFov = uAspectFov.y * 0.5;
    float tanFov = tan(halfFov);
    vec3 viewDir = normalize(vec3(
      vScreenPos.x * tanFov * uAspectFov.x,
      vScreenPos.y * tanFov,
      -1.0
    ));

    // Apply camera rotation: pitch → bearing → lat → lng
    // This matches MapLibre's camera model
    float lat = uCamera.x, lng = uCamera.y, bearing = uCamera.z, pitch = uCamera.w;
    vec3 dir = viewDir;
    dir = rotX(-pitch, dir);
    dir = rotZ(bearing, dir);
    dir = rotX(-lat, dir);
    dir = rotY(lng, dir);

    // Rotate -90° around X: astronomical Z-up → Y-up (matches Three.js skybox)
    dir = vec3(dir.x, -dir.z, dir.y);

    // Sample the correct cubemap face
    vec3 absDir = abs(dir);
    vec2 uv;
    int face;
    if (absDir.x >= absDir.y && absDir.x >= absDir.z) {
      face = dir.x > 0.0 ? 0 : 1;
      uv = dir.x > 0.0
        ? vec2(-dir.z / absDir.x, -dir.y / absDir.x)
        : vec2(dir.z / absDir.x, -dir.y / absDir.x);
    } else if (absDir.y >= absDir.x && absDir.y >= absDir.z) {
      face = dir.y > 0.0 ? 2 : 3;
      uv = dir.y > 0.0
        ? vec2(dir.x / absDir.y, dir.z / absDir.y)
        : vec2(dir.x / absDir.y, -dir.z / absDir.y);
    } else {
      face = dir.z > 0.0 ? 4 : 5;
      uv = dir.z > 0.0
        ? vec2(dir.x / absDir.z, -dir.y / absDir.z)
        : vec2(-dir.x / absDir.z, -dir.y / absDir.z);
    }
    uv = uv * 0.5 + 0.5;

    vec3 color = vec3(0.0);
    if (face == 0) color = texture(uFaces[0], uv).rgb;
    else if (face == 1) color = texture(uFaces[1], uv).rgb;
    else if (face == 2) color = texture(uFaces[2], uv).rgb;
    else if (face == 3) color = texture(uFaces[3], uv).rgb;
    else if (face == 4) color = texture(uFaces[4], uv).rgb;
    else color = texture(uFaces[5], uv).rgb;

    fragColor = vec4(color * uBrightness, 1.0);
  }
`

// Dataset overlay: simple textured sphere (proper equirectangular mapping)
const datasetVertSrc = `#version 300 es
  layout(location = 0) in vec3 aPosition;
  layout(location = 2) in vec2 aUV;
  uniform mat4 uMatrix;
  uniform float uRadiusScale;
  out vec2 vUV;

  void main() {
    vUV = aUV;
    gl_Position = uMatrix * vec4(aPosition * uRadiusScale, 1.0);
  }
`

// Dataset overlay fragment shader. Phase 3e/B extends the simple
// `texture(uDatasetTex, vUV)` passthrough with three modes driven
// by per-dataset options the loader passes in (sourced from the
// Phase 3d catalog metadata):
//
//   - `uHasBbox`: when true, the dataset is treated as a regional
//     overlay clipped to `uBbox = (n, s, w, e)` in degrees.
//     Fragments outside the box are discarded so the base raster
//     layer shows through; fragments inside are remapped so the
//     texture STRETCHES to the bbox extent. Antimeridian-crossing
//     boxes (w > e) are handled — e.g. a Pacific window from
//     w=170 to e=-170.
//
//   - `uLonOrigin` (non-bbox path only): shifts the U coordinate
//     so a globally-equirectangular texture centered on the
//     dateline (lonOrigin=±180) wraps correctly. lonOrigin=0
//     reduces to vUV.x passthrough so legacy datasets render
//     identically to before 3e/B.
//
//   - `uFlipY`: applied last in both paths, flips the V axis for
//     datasets authored with inverted-Y conventions. Zero SOS
//     rows use this today but the field is wired for future
//     publishers.
//
//   - `uDataEncoded`: when true the texture is not a picture. Its
//     red channel carries the normalised value (0 = vmin / no data,
//     1 = vmax) and `uColorLut` — a 256×1 RGBA ramp built from the
//     dataset's sidecar — turns it back into colour and alpha. The
//     luma is read as a raw code value: the texture is uploaded as
//     plain `gl.RGBA` with no sRGB internal format, so no transfer
//     function has been applied and none must be. Everything before
//     this point is shared with the picture path, so a legacy
//     dataset compiles the same shader and takes the same branch it
//     always has.
const datasetFragSrc = `#version 300 es
  precision highp float;
  uniform sampler2D uDatasetTex;
  uniform bool uHasBbox;
  uniform vec4 uBbox;        // (n, s, w, e) degrees
  uniform float uLonOrigin;  // degrees
  uniform bool uFlipY;
  uniform bool uDataEncoded;
  uniform sampler2D uColorLut; // 256x1 RGBA palette
  in vec2 vUV;
  out vec4 fragColor;

  void main() {
    // vUV.y == 0 at north pole, == 1 at south pole (sphere geometry
    // convention). Derive geographic lat/lon so the bbox math
    // doesn't have to reason in texture space.
    float lat = (0.5 - vUV.y) * 180.0;
    float lon = (vUV.x - 0.5) * 360.0;

    vec2 sampleUV;
    if (uHasBbox) {
      float n = uBbox.x;
      float s = uBbox.y;
      float w = uBbox.z;
      float e = uBbox.w;

      // Latitude clip: outside [s, n] → base layer shows through.
      if (lat > n || lat < s) {
        discard;
      }

      float u;
      if (w <= e) {
        // Normal (non-wrapping) box.
        if (lon < w || lon > e) {
          discard;
        }
        u = (lon - w) / (e - w);
      } else {
        // Antimeridian-crossing box: a fragment is inside if its
        // longitude is east of w OR west of e.
        float span = (360.0 - w) + e;
        if (lon >= w) {
          u = (lon - w) / span;
        } else if (lon <= e) {
          u = (lon + 360.0 - w) / span;
        } else {
          discard;
        }
      }

      // Latitude → V with image convention (n on top → V=0).
      float v = (n - lat) / (n - s);
      if (uFlipY) v = 1.0 - v;
      sampleUV = vec2(u, v);
    } else {
      // Full-globe path. lonOrigin shifts the U axis; fract()
      // wraps so a sample at lon < lonOrigin pulls from the
      // texture's right edge (and vice versa).
      float u = fract((lon - uLonOrigin) / 360.0 + 0.5);
      float v = uFlipY ? (1.0 - vUV.y) : vUV.y;
      sampleUV = vec2(u, v);
    }

    if (uDataEncoded) {
      // Luma is the measurement. Look the value up in the palette
      // rather than showing it, and let the palette's own alpha
      // decide what the base map shows through — which is exact
      // here, because "no data" was encoded as a value rather than
      // baked into a colour.
      float t = texture(uDatasetTex, sampleUV).r;
      fragColor = texture(uColorLut, vec2(t, 0.5));
    } else {
      fragColor = texture(uDatasetTex, sampleUV);
    }
  }
`

// --- Sun sprite shaders ---
// Renders a billboard glow at the sun's projected screen position.
// Vertex shader positions a screen-aligned quad; fragment draws radial gradient.

const sunVertSrc = `#version 300 es
  uniform vec4 uSunClip;  // sun position in clip space (x, y, z, w)
  uniform float uScale;   // quad half-size in clip space
  uniform float uAspect;  // viewport width / height
  out vec2 vOffset;        // [-1,1] offset from center
  void main() {
    // Screen-aligned quad from vertex ID (0-5 → 2 triangles)
    vec2 corners[6] = vec2[6](
      vec2(-1,-1), vec2(1,-1), vec2(1,1),
      vec2(-1,-1), vec2(1,1), vec2(-1,1)
    );
    vOffset = corners[gl_VertexID];
    vec2 ndcSun = uSunClip.xy / uSunClip.w;
    float ndcZ = uSunClip.z / uSunClip.w; // 1.0 (same as skybox — occluded by globe)
    // Correct for aspect ratio so the quad stays circular
    vec2 scaledOffset = vOffset * uScale;
    scaledOffset.x /= uAspect;
    gl_Position = vec4(ndcSun + scaledOffset, ndcZ, 1.0);
  }
`

const sunFragSrc = `#version 300 es
  precision highp float;
  in vec2 vOffset;
  uniform float uCoreRadius;
  uniform float uGlowFalloff;
  uniform vec3 uCoreColor;
  uniform vec3 uGlowColor;
  out vec4 fragColor;
  void main() {
    float d = length(vOffset);
    // Hard-edge discard outside unit circle
    if (d > 1.0) discard;
    // Core: bright center with smooth falloff
    float core = smoothstep(uCoreRadius + 0.05, uCoreRadius - 0.02, d);
    // Glow: wide soft falloff
    float glow = exp(-d * d * uGlowFalloff);
    vec3 color = uCoreColor * core + uGlowColor * glow;
    float alpha = max(core, glow * 0.5);
    fragColor = vec4(color * alpha, alpha);
  }
`

// Sun sprite constants
const SUN_WORLD_DISTANCE = 8.0    // distance from globe center in ECEF units
const SUN_SCREEN_SCALE = 0.12     // base quad half-size in NDC
const SUN_CORE_RADIUS = 0.12
const SUN_GLOW_FALLOFF = 3.5
const SUN_CORE_COLOR: [number, number, number] = [1.0, 0.98, 0.9]    // warm white
const SUN_GLOW_COLOR: [number, number, number] = [1.0, 0.82, 0.55]   // warm orange

interface SunProgram {
  program: WebGLProgram
  sunClipLoc: WebGLUniformLocation | null
  scaleLoc: WebGLUniformLocation | null
  aspectLoc: WebGLUniformLocation | null
  coreRadiusLoc: WebGLUniformLocation | null
  glowFalloffLoc: WebGLUniformLocation | null
  coreColorLoc: WebGLUniformLocation | null
  glowColorLoc: WebGLUniformLocation | null
}

// --- Atmosphere light sync ---

/**
 * Set MapLibre's light once using anchor:'map'.
 *
 * With anchor:'map', MapLibre automatically applies camera rotations
 * each frame — no per-frame setLight() calls needed, so zero lag.
 *
 * MapLibre's getSunPos pipeline for anchor:'map':
 *   cart = sphericalToCartesian(r, az, polar)
 *   lp = [-cart.x, -cart.y, -cart.z]   (negate)
 *   lp = ... * Rx(lat) * Ry(-lng) * lp (camera rotation)
 *   → used as sun direction in atmosphere shader
 *
 * We want the final result to equal our ECEF sun direction. Since MapLibre
 * applies the camera rotation automatically, we just need:
 *   -cart = sunECEF  →  cart = -sunECEF
 * Then solve for [azimuthal, polar] from cart = -sunECEF.
 */
export function syncAtmosphereLight(map: MaplibreMap, sunLat: number, sunLng: number): void {
  const sLatR = sunLat * Math.PI / 180
  const sLngR = sunLng * Math.PI / 180

  // cart = -sunECEF (so that negate(cart) = sunECEF)
  const cx = -(Math.cos(sLatR) * Math.sin(sLngR))
  const cy = -(Math.sin(sLatR))
  const cz = -(Math.cos(sLatR) * Math.cos(sLngR))

  // Solve: cart = sphericalToCartesian(r, az, polar)
  // where az_internal = (az + 90) * π/180
  //   x = r * cos(az_internal) * sin(polar_rad)
  //   y = r * sin(az_internal) * sin(polar_rad)
  //   z = r * cos(polar_rad)
  const r = Math.sqrt(cx * cx + cy * cy + cz * cz)
  if (r < 1e-10) return

  const polar = Math.acos(Math.max(-1, Math.min(1, cz / r))) * 180 / Math.PI
  const azInternal = Math.atan2(cy, cx)
  const azimuthal = ((azInternal * 180 / Math.PI - 90) % 360 + 360) % 360

  map.setLight({
    anchor: 'map',
    position: [1.5, azimuthal, polar],
  })
}

/** Compute MapLibre light position for a given sun lat/lng. */
export function computeSunLightPosition(sunLat: number, sunLng: number): [number, number, number] {
  const sLatR = sunLat * Math.PI / 180
  const sLngR = sunLng * Math.PI / 180
  const cx = -(Math.cos(sLatR) * Math.sin(sLngR))
  const cy = -(Math.sin(sLatR))
  const cz = -(Math.cos(sLatR) * Math.cos(sLngR))
  const r = Math.sqrt(cx * cx + cy * cy + cz * cz)
  if (r < 1e-10) return [1.5, 0, 90]
  const polar = Math.acos(Math.max(-1, Math.min(1, cz / r))) * 180 / Math.PI
  const azInternal = Math.atan2(cy, cx)
  const azimuthal = ((azInternal * 180 / Math.PI - 90) % 360 + 360) % 360
  return [1.5, azimuthal, polar]
}

/**
 * Filtering for a data-encoded dataset texture: nearest, no mipmaps.
 *
 * The same argument that puts `flags=neighbor` in the encoder. A
 * bilinear tap averages the codes of adjacent texels, and across a
 * nodata/data edge that average is a value nobody measured — it lands
 * mid-palette and paints a fringe of invented colour around every
 * coastline of the data. Mipmaps are worse: each level averages four
 * codes, so a zoomed-out globe would read from a texture whose
 * "values" are entirely synthetic.
 *
 * The cost is visible texel edges when magnified past 1:1. That is the
 * right trade for a measurement, and it is the honest picture of the
 * data's real resolution.
 */
function applyDataEncodedFiltering(gl: WebGL2RenderingContext): void {
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST)
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST)
}

/**
 * Create, update, or tear down the 256×1 palette texture to match the
 * dataset being loaded. Returns the texture, or `null` when the
 * dataset is an ordinary picture — the caller treats that `null` as
 * the mode flag, so a legacy dataset can never end up in the
 * data-encoded branch.
 *
 * Leaves `TEXTURE_2D` bound to whatever the caller had bound, since
 * both call sites are mid-way through configuring the dataset texture.
 */
function syncColorLut(
  gl: WebGL2RenderingContext,
  existing: WebGLTexture | null,
  options?: DatasetOverlayOptions,
  display: ColorScaleDisplay = DEFAULT_DISPLAY,
): WebGLTexture | null {
  const scale = options?.colorScale
  if (!scale) {
    if (existing) gl.deleteTexture(existing)
    return null
  }
  const bound = gl.getParameter(gl.TEXTURE_BINDING_2D) as WebGLTexture | null
  const lut = existing ?? gl.createTexture()
  gl.bindTexture(gl.TEXTURE_2D, lut)
  gl.texImage2D(
    gl.TEXTURE_2D, 0, gl.RGBA, COLOR_SCALE_LUT_SIZE, 1, 0,
    gl.RGBA, gl.UNSIGNED_BYTE, buildDisplayLut(scale, display),
  )
  // LINEAR across the ramp is correct and wanted: the palette is a
  // continuous colour ramp, unlike the data it is applied to.
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR)
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR)
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE)
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE)
  gl.bindTexture(gl.TEXTURE_2D, bound)
  return lut
}

// --- Layer implementation ---

interface DatasetProgram {
  program: WebGLProgram
  matrixLoc: WebGLUniformLocation | null
  texLoc: WebGLUniformLocation | null
  radiusScaleLoc: WebGLUniformLocation | null
  /** Phase 3e/B per-dataset overlay uniforms. */
  hasBboxLoc: WebGLUniformLocation | null
  bboxLoc: WebGLUniformLocation | null
  lonOriginLoc: WebGLUniformLocation | null
  flipYLoc: WebGLUniformLocation | null
  /** Data-encoded video uniforms. */
  dataEncodedLoc: WebGLUniformLocation | null
  colorLutLoc: WebGLUniformLocation | null
}

interface DayNightProgram {
  program: WebGLProgram
  matrixLoc: WebGLUniformLocation | null
  sunDirLoc: WebGLUniformLocation | null
  radiusScaleLoc: WebGLUniformLocation | null
}

interface ColorCorrectProgram {
  program: WebGLProgram
  matrixLoc: WebGLUniformLocation | null
  radiusScaleLoc: WebGLUniformLocation | null
  framebufferLoc: WebGLUniformLocation | null
  viewportSizeLoc: WebGLUniformLocation | null
  contrastLoc: WebGLUniformLocation | null
  saturationLoc: WebGLUniformLocation | null
}

interface BumpProgram {
  program: WebGLProgram
  matrixLoc: WebGLUniformLocation | null
  radiusScaleLoc: WebGLUniformLocation | null
  sunDirLoc: WebGLUniformLocation | null
  normalMapLoc: WebGLUniformLocation | null
  strengthLoc: WebGLUniformLocation | null
}

interface LightsProgram extends DayNightProgram {
  blackMarbleLoc: WebGLUniformLocation | null
  viewportSizeLoc: WebGLUniformLocation | null
  strengthLoc: WebGLUniformLocation | null
}

interface SpecularProgram extends DayNightProgram {
  specMapLoc: WebGLUniformLocation | null
  cloudMaskLoc: WebGLUniformLocation | null
  viewDirLoc: WebGLUniformLocation | null
  shininessLoc: WebGLUniformLocation | null
  strengthLoc: WebGLUniformLocation | null
  cloudAlphaGammaLoc: WebGLUniformLocation | null
  cloudZoomFadeLoc: WebGLUniformLocation | null
}

interface CloudsProgram extends DayNightProgram {
  cloudTexLoc: WebGLUniformLocation | null
  radiusLoc: WebGLUniformLocation | null
  opacityLoc: WebGLUniformLocation | null
  alphaGammaLoc: WebGLUniformLocation | null
  nightDarkeningLoc: WebGLUniformLocation | null
  zoomFadeLoc: WebGLUniformLocation | null
}

function compileProgram(
  gl: WebGL2RenderingContext,
  vsSrc: string,
  fsSrc: string,
  label: string,
): WebGLProgram | null {
  const vs = gl.createShader(gl.VERTEX_SHADER)!
  gl.shaderSource(vs, vsSrc)
  gl.compileShader(vs)
  if (!gl.getShaderParameter(vs, gl.COMPILE_STATUS)) {
    const info = gl.getShaderInfoLog(vs) ?? ''
    logger.warn(`[EarthTileLayer] ${label} vertex shader:`, info)
    reportError('tile', new Error(`${label} vertex shader compile failed: ${info}`))
    return null
  }

  const fs = gl.createShader(gl.FRAGMENT_SHADER)!
  gl.shaderSource(fs, fsSrc)
  gl.compileShader(fs)
  if (!gl.getShaderParameter(fs, gl.COMPILE_STATUS)) {
    const info = gl.getShaderInfoLog(fs) ?? ''
    logger.warn(`[EarthTileLayer] ${label} fragment shader:`, info)
    reportError('tile', new Error(`${label} fragment shader compile failed: ${info}`))
    return null
  }

  const prog = gl.createProgram()!
  gl.attachShader(prog, vs)
  gl.attachShader(prog, fs)
  gl.linkProgram(prog)
  gl.deleteShader(vs)
  gl.deleteShader(fs)

  if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) {
    const info = gl.getProgramInfoLog(prog) ?? ''
    logger.warn(`[EarthTileLayer] ${label} link:`, info)
    reportError('tile', new Error(`${label} program link failed: ${info}`))
    return null
  }
  return prog
}

/** Extended interface for controlling the earth tile layer from MapRenderer. */
export interface EarthTileLayerControl {
  /** The MapLibre custom layer interface (2d — renders below labels). */
  layer: CustomLayerInterface
  /** Capture layer (2d — snapshots Black Marble tiles between BM and BM layers). */
  captureLayer: CustomLayerInterface
  /** Skybox layer (3d — renders after everything, uses depth test for stars). */
  skyboxLayer: CustomLayerInterface
  /** Resolves when all textures (night lights, specular, clouds) have loaded. */
  ready: Promise<void>
  /** Update sun direction. Called by MapRenderer.enableSunLighting(). */
  setSunPosition(lat: number, lng: number): void
  /** Clear sun override — reverts to real-time sun position. */
  clearSunOverride(): void
  /** Show or hide all earth effects (day/night, lights, specular, clouds). */
  setVisible(visible: boolean): void
  /** Inflate effects sphere to cover exaggerated terrain. 0 = no terrain. */
  setTerrainExaggeration(exaggeration: number): void
  /** Display an equirectangular image as a dataset overlay on the globe.
   * `options` carries Phase 3d metadata (bbox / lonOrigin / isFlippedInY)
   * that the dataset-overlay shader applies in 3e/B; until then the
   * options are stored but the rendering is unchanged. */
  setDatasetTexture(
    image: HTMLCanvasElement | HTMLImageElement,
    options?: DatasetOverlayOptions,
  ): void
  /** Display an equirectangular video as a dataset overlay on the globe. */
  setDatasetVideo(video: HTMLVideoElement, options?: DatasetOverlayOptions): void
  /** Force a one-shot video texture re-upload (e.g. after scrubbing while paused). */
  requestVideoUpdate(): void
  /** Apply a palette / stretch / threshold transform to the current
   *  data-encoded dataset. Rebuilds the 256x1 LUT only — the dataset
   *  texture is untouched, so this is one upload and costs nothing per
   *  frame. A no-op for picture datasets, which have no LUT. */
  setColorScaleDisplay(display: ColorScaleDisplay): void
  /** Remove the current dataset overlay (image or video). */
  clearDatasetTexture(): void
}

/**
 * Create a MapLibre CustomLayerInterface that composites day/night shading,
 * city lights, specular glint, and clouds onto the globe tiles.
 */
export function createEarthTileLayer(): EarthTileLayerControl {
  let darken: DayNightProgram | null = null
  let lights: LightsProgram | null = null
  let specular: SpecularProgram | null = null
  let clouds: CloudsProgram | null = null
  let atmosphere: AtmosphereProgram | null = null
  let colorCorrect: ColorCorrectProgram | null = null
  let bump: BumpProgram | null = null
  let bumpTex: WebGLTexture | null = null
  let bumpTexReady = false
  let vao: WebGLVertexArrayObject | null = null
  let indexCount = 0
  let captureTex: WebGLTexture | null = null
  let captureW = 0, captureH = 0
  // Separate framebuffer-capture texture for the §7.2 colour-correction
  // pre-pass. Distinct from `captureTex` (Black Marble) — that one is
  // populated by the standalone capture layer between Black Marble and
  // Blue Marble, while this one is filled at the start of THIS layer's
  // render() after Blue Marble has drawn.
  let baseTex: WebGLTexture | null = null
  let baseW = 0, baseH = 0
  let specTex: WebGLTexture | null = null
  let specTexReady = false
  let cloudTex: WebGLTexture | null = null
  let cloudTexReady = false
  let disposed = false
  let dataset: DatasetProgram | null = null
  let datasetTex: WebGLTexture | null = null
  let datasetVideo: HTMLVideoElement | null = null
  let datasetActive = false
  // Phase 3e: per-dataset overlay options. Stored verbatim from the
  // caller; the dataset-overlay fragment shader will read them in
  // 3e/B to clip to bbox / shift U by lonOrigin / flip V when
  // isFlippedInY is set. Until 3e/B lands, the options are
  // captured but the rendering ignores them — pure plumbing.
  let datasetOptions: DatasetOverlayOptions | null = null
  /** 256x1 RGBA palette for a data-encoded dataset, or null when the
   *  texture is an ordinary picture. Doubles as the mode flag. */
  let datasetColorLut: WebGLTexture | null = null
  /** Viewing state, not dataset state: the palette / stretch / threshold
   *  the user has chosen. Deliberately NOT reset when the dataset
   *  changes — a viewer who picked a colourblind-safe ramp expects it to
   *  survive the next dataset, the way the labels and borders toggles
   *  do. `docs/DATA_ANALYSIS_PLAN.md` §A1. */
  let datasetDisplay: ColorScaleDisplay = DEFAULT_DISPLAY
  let forceVideoUpdate = false
  let transmittanceLutTex: WebGLTexture | null = null
  let skyboxProg: WebGLProgram | null = null
  let skyboxInvProjLoc: WebGLUniformLocation | null = null
  let skyboxCameraLoc: WebGLUniformLocation | null = null
  let skyboxAspectFovLoc: WebGLUniformLocation | null = null
  let skyboxBrightnessLoc: WebGLUniformLocation | null = null
  let skyboxFaceLocs: (WebGLUniformLocation | null)[] = []
  let skyboxFaceTextures: (WebGLTexture | null)[] = []
  let skyboxReady = false
  let sunProg: SunProgram | null = null
  let glRef: WebGL2RenderingContext | null = null
  let mapRef: MaplibreMap | null = null

  // Unsubscribe handle for shader-settings change events (§7.2).
  // Returned by onShaderSettingsChange in onAdd; called in onRemove
  // to drop the listener when the layer detaches.
  let unsubscribeShaderSettings: (() => void) | null = null

  // Sun direction — updated each frame from real time, or set externally
  let sunDir: [number, number, number] = [1, 0, 0]
  let sunOverride: { lat: number; lng: number } | null = null
  let visible = true

  // Scratch buffer reused each frame by the atmosphere pass to derive
  // the camera position in ECEF unit-sphere coords. Lives in closure
  // scope so per-frame work doesn't allocate.
  const cameraEcefScratch: [number, number, number] = [0, 0, 0]

  // Terrain-aware radius scale — inflates effects sphere to cover exaggerated terrain.
  // Max Earth elevation ~8848m / 6371km radius ≈ 0.00139 normalized.
  // Scale = 1.0 + 0.002 * exaggeration (with safety margin).
  let terrainRadiusScale = 1.0

  // Readiness tracking — resolves when all textures are loaded
  let resolveReady: () => void
  const readyPromise = new Promise<void>(r => { resolveReady = r })
  const checkReady = () => {
    if (specTexReady && cloudTexReady) resolveReady()
  }

  const layer: CustomLayerInterface = {
    id: 'earth-tile-layer',
    type: 'custom',
    renderingMode: '2d',

    onAdd(_map: MaplibreMap, gl: WebGL2RenderingContext | WebGLRenderingContext) {
      mapRef = _map

      // Set atmosphere light once — with anchor:'map', MapLibre handles
      // camera rotation automatically so no per-frame updates needed.
      const initSun = getSunPosition(new Date())
      syncAtmosphereLight(_map, initSun.lat, initSun.lng)

      // Subscribe to shader-settings changes (§7.2) so a Tools-menu
      // specular preset click — or a tuner-page slider drag — re-
      // renders the globe without waiting for the next camera motion.
      // The render() function reads the live snapshot each frame, so
      // we don't need to push uniform values here; just trigger paint.
      unsubscribeShaderSettings = onShaderSettingsChange(() => {
        _map.triggerRepaint()
      })

      const gl2 = gl as WebGL2RenderingContext
      glRef = gl2

      // --- Compile skybox shader and load cubemap faces ---
      skyboxProg = compileProgram(gl2, skyboxVertSrc, skyboxFragSrc, 'skybox')
      if (skyboxProg) {
        skyboxInvProjLoc = gl2.getUniformLocation(skyboxProg, 'uInvProjMatrix')
        skyboxCameraLoc = gl2.getUniformLocation(skyboxProg, 'uCamera')
        skyboxAspectFovLoc = gl2.getUniformLocation(skyboxProg, 'uAspectFov')
        skyboxBrightnessLoc = gl2.getUniformLocation(skyboxProg, 'uBrightness')
        skyboxFaceLocs = SKYBOX_FACES.map((_, i) =>
          gl2.getUniformLocation(skyboxProg!, `uFaces[${i}]`))

        // Load 6 face textures — deferred so they don't compete with tile bandwidth
        let facesLoaded = 0
        skyboxFaceTextures = SKYBOX_FACES.map((_face, _i) => {
          const tex = gl2.createTexture()
          gl2.bindTexture(gl2.TEXTURE_2D, tex)
          gl2.texImage2D(gl2.TEXTURE_2D, 0, gl2.RGBA, 1, 1, 0, gl2.RGBA, gl2.UNSIGNED_BYTE,
            new Uint8Array([0, 0, 0, 255]))
          return tex
        })
        const loadSkyboxFaces = () => {
          if (disposed) return
          SKYBOX_FACES.forEach((face, i) => {
            const tex = skyboxFaceTextures![i]
            const faceImg = new Image()
            faceImg.crossOrigin = 'anonymous'
            faceImg.onload = () => {
              if (disposed) return
              gl2.bindTexture(gl2.TEXTURE_2D, tex)
              gl2.texImage2D(gl2.TEXTURE_2D, 0, gl2.RGBA, gl2.RGBA, gl2.UNSIGNED_BYTE, faceImg)
              gl2.texParameteri(gl2.TEXTURE_2D, gl2.TEXTURE_MIN_FILTER, gl2.LINEAR)
              gl2.texParameteri(gl2.TEXTURE_2D, gl2.TEXTURE_MAG_FILTER, gl2.LINEAR)
              gl2.texParameteri(gl2.TEXTURE_2D, gl2.TEXTURE_WRAP_S, gl2.CLAMP_TO_EDGE)
              gl2.texParameteri(gl2.TEXTURE_2D, gl2.TEXTURE_WRAP_T, gl2.CLAMP_TO_EDGE)
              facesLoaded++
              if (facesLoaded === 6) {
                skyboxReady = true
                _map.triggerRepaint()
                logger.info('[EarthTileLayer] Skybox loaded (6 faces)')
              }
            }
            faceImg.src = SKYBOX_URL_BASE + face + '.jpg'
          })
        }
        typeof requestIdleCallback !== 'undefined'
          ? requestIdleCallback(loadSkyboxFaces, { timeout: 2000 })
          : setTimeout(loadSkyboxFaces, 200)
      }

      // --- Compile sun sprite shader ---
      const sunProgram = compileProgram(gl2, sunVertSrc, sunFragSrc, 'sun')
      if (sunProgram) {
        sunProg = {
          program: sunProgram,
          sunClipLoc: gl2.getUniformLocation(sunProgram, 'uSunClip'),
          scaleLoc: gl2.getUniformLocation(sunProgram, 'uScale'),
          aspectLoc: gl2.getUniformLocation(sunProgram, 'uAspect'),
          coreRadiusLoc: gl2.getUniformLocation(sunProgram, 'uCoreRadius'),
          glowFalloffLoc: gl2.getUniformLocation(sunProgram, 'uGlowFalloff'),
          coreColorLoc: gl2.getUniformLocation(sunProgram, 'uCoreColor'),
          glowColorLoc: gl2.getUniformLocation(sunProgram, 'uGlowColor'),
        }
      }

      // --- Compile dataset overlay shader ---
      const datasetProg = compileProgram(gl2, datasetVertSrc, datasetFragSrc, 'dataset')
      if (datasetProg) {
        dataset = {
          program: datasetProg,
          matrixLoc: gl2.getUniformLocation(datasetProg, 'uMatrix'),
          texLoc: gl2.getUniformLocation(datasetProg, 'uDatasetTex'),
          radiusScaleLoc: gl2.getUniformLocation(datasetProg, 'uRadiusScale'),
          hasBboxLoc: gl2.getUniformLocation(datasetProg, 'uHasBbox'),
          bboxLoc: gl2.getUniformLocation(datasetProg, 'uBbox'),
          lonOriginLoc: gl2.getUniformLocation(datasetProg, 'uLonOrigin'),
          flipYLoc: gl2.getUniformLocation(datasetProg, 'uFlipY'),
          dataEncodedLoc: gl2.getUniformLocation(datasetProg, 'uDataEncoded'),
          colorLutLoc: gl2.getUniformLocation(datasetProg, 'uColorLut'),
        }
      }

      // --- Compile all earth effect shader programs ---
      const colorCorrectProg = compileProgram(
        gl2,
        colorCorrectVertSrc,
        colorCorrectFragSrc,
        'colorCorrect',
      )
      if (colorCorrectProg) {
        colorCorrect = {
          program: colorCorrectProg,
          matrixLoc: gl2.getUniformLocation(colorCorrectProg, 'uMatrix'),
          radiusScaleLoc: gl2.getUniformLocation(colorCorrectProg, 'uRadiusScale'),
          framebufferLoc: gl2.getUniformLocation(colorCorrectProg, 'uFramebuffer'),
          viewportSizeLoc: gl2.getUniformLocation(colorCorrectProg, 'uViewportSize'),
          contrastLoc: gl2.getUniformLocation(colorCorrectProg, 'uContrast'),
          saturationLoc: gl2.getUniformLocation(colorCorrectProg, 'uSaturation'),
        }
      }

      const bumpProg = compileProgram(gl2, bumpVertSrc, bumpFragSrc, 'bump')
      if (bumpProg) {
        bump = {
          program: bumpProg,
          matrixLoc: gl2.getUniformLocation(bumpProg, 'uMatrix'),
          radiusScaleLoc: gl2.getUniformLocation(bumpProg, 'uRadiusScale'),
          sunDirLoc: gl2.getUniformLocation(bumpProg, 'uSunDir'),
          normalMapLoc: gl2.getUniformLocation(bumpProg, 'uNormalMap'),
          strengthLoc: gl2.getUniformLocation(bumpProg, 'uStrength'),
        }
      }

      const darkenProg = compileProgram(gl2, darkenVertSrc, darkenFragSrc, 'darken')
      if (darkenProg) {
        darken = {
          program: darkenProg,
          matrixLoc: gl2.getUniformLocation(darkenProg, 'uMatrix'),
          sunDirLoc: gl2.getUniformLocation(darkenProg, 'uSunDir'),
          radiusScaleLoc: gl2.getUniformLocation(darkenProg, 'uRadiusScale'),
        }
      }

      const lightsProg = compileProgram(gl2, lightsVertSrc, lightsFragSrc, 'lights')
      if (lightsProg) {
        lights = {
          program: lightsProg,
          matrixLoc: gl2.getUniformLocation(lightsProg, 'uMatrix'),
          sunDirLoc: gl2.getUniformLocation(lightsProg, 'uSunDir'),
          blackMarbleLoc: gl2.getUniformLocation(lightsProg, 'uBlackMarble'),
          viewportSizeLoc: gl2.getUniformLocation(lightsProg, 'uViewportSize'),
          strengthLoc: gl2.getUniformLocation(lightsProg, 'uLightStrength'),
          radiusScaleLoc: gl2.getUniformLocation(lightsProg, 'uRadiusScale'),
        }
      }

      const cloudsProg = compileProgram(gl2, cloudsVertSrc, cloudsFragSrc, 'clouds')
      if (cloudsProg) {
        clouds = {
          program: cloudsProg,
          matrixLoc: gl2.getUniformLocation(cloudsProg, 'uMatrix'),
          sunDirLoc: gl2.getUniformLocation(cloudsProg, 'uSunDir'),
          radiusScaleLoc: null, // clouds use uRadius instead
          cloudTexLoc: gl2.getUniformLocation(cloudsProg, 'uCloudTex'),
          radiusLoc: gl2.getUniformLocation(cloudsProg, 'uRadius'),
          opacityLoc: gl2.getUniformLocation(cloudsProg, 'uOpacity'),
          alphaGammaLoc: gl2.getUniformLocation(cloudsProg, 'uAlphaGamma'),
          nightDarkeningLoc: gl2.getUniformLocation(cloudsProg, 'uNightDarkening'),
          zoomFadeLoc: gl2.getUniformLocation(cloudsProg, 'uZoomFade'),
        }
      }

      const specularProg = compileProgram(gl2, specularVertSrc, specularFragSrc, 'specular')
      if (specularProg) {
        specular = {
          program: specularProg,
          matrixLoc: gl2.getUniformLocation(specularProg, 'uMatrix'),
          sunDirLoc: gl2.getUniformLocation(specularProg, 'uSunDir'),
          radiusScaleLoc: gl2.getUniformLocation(specularProg, 'uRadiusScale'),
          specMapLoc: gl2.getUniformLocation(specularProg, 'uSpecMap'),
          cloudMaskLoc: gl2.getUniformLocation(specularProg, 'uCloudMask'),
          viewDirLoc: gl2.getUniformLocation(specularProg, 'uViewDir'),
          shininessLoc: gl2.getUniformLocation(specularProg, 'uShininess'),
          strengthLoc: gl2.getUniformLocation(specularProg, 'uStrength'),
          cloudAlphaGammaLoc: gl2.getUniformLocation(specularProg, 'uCloudAlphaGamma'),
          cloudZoomFadeLoc: gl2.getUniformLocation(specularProg, 'uCloudZoomFade'),
        }
      }

      const atmosphereProg = compileProgram(gl2, atmosphereVertSrc, atmosphereFragSrc, 'atmosphere')
      if (atmosphereProg) {
        atmosphere = {
          program: atmosphereProg,
          matrixLoc: gl2.getUniformLocation(atmosphereProg, 'uMatrix'),
          radiusScaleLoc: gl2.getUniformLocation(atmosphereProg, 'uRadiusScale'),
          sunDirLoc: gl2.getUniformLocation(atmosphereProg, 'uSunDir'),
          cameraPosKmLoc: gl2.getUniformLocation(atmosphereProg, 'uCameraPosKm'),
          intensityLoc: gl2.getUniformLocation(atmosphereProg, 'uIntensity'),
          transmittanceLutLoc: gl2.getUniformLocation(atmosphereProg, 'uTransmittanceLut'),
        }
      }

      // --- Build the transmittance LUT and upload as a texture ---
      // CPU-side compute keeps this independent of MapLibre's render
      // lifecycle. Tens-of-ms one-time cost on app start.
      {
        const lut = computeTransmittanceLut()
        transmittanceLutTex = gl2.createTexture()
        gl2.bindTexture(gl2.TEXTURE_2D, transmittanceLutTex)
        gl2.texImage2D(
          gl2.TEXTURE_2D, 0, gl2.RGBA, lut.width, lut.height, 0,
          gl2.RGBA, gl2.UNSIGNED_BYTE, lut.pixels,
        )
        gl2.texParameteri(gl2.TEXTURE_2D, gl2.TEXTURE_MIN_FILTER, gl2.LINEAR)
        gl2.texParameteri(gl2.TEXTURE_2D, gl2.TEXTURE_MAG_FILTER, gl2.LINEAR)
        gl2.texParameteri(gl2.TEXTURE_2D, gl2.TEXTURE_WRAP_S, gl2.CLAMP_TO_EDGE)
        gl2.texParameteri(gl2.TEXTURE_2D, gl2.TEXTURE_WRAP_T, gl2.CLAMP_TO_EDGE)
      }

      // --- Generate sphere geometry ---
      const sphere = createSphereGeometry(1.0, 64, 64)
      indexCount = sphere.indexCount

      vao = gl2.createVertexArray()
      gl2.bindVertexArray(vao)

      // Position buffer (used by both programs at location 0)
      const posBuf = gl2.createBuffer()
      gl2.bindBuffer(gl2.ARRAY_BUFFER, posBuf)
      gl2.bufferData(gl2.ARRAY_BUFFER, sphere.positions, gl2.STATIC_DRAW)
      gl2.enableVertexAttribArray(0)
      gl2.vertexAttribPointer(0, 3, gl2.FLOAT, false, 0, 0)

      // Normal buffer (location 1)
      const normBuf = gl2.createBuffer()
      gl2.bindBuffer(gl2.ARRAY_BUFFER, normBuf)
      gl2.bufferData(gl2.ARRAY_BUFFER, sphere.normals, gl2.STATIC_DRAW)
      gl2.enableVertexAttribArray(1)
      gl2.vertexAttribPointer(1, 3, gl2.FLOAT, false, 0, 0)

      // UV buffer (location 2 — only used by lights program)
      const uvBuf = gl2.createBuffer()
      gl2.bindBuffer(gl2.ARRAY_BUFFER, uvBuf)
      gl2.bufferData(gl2.ARRAY_BUFFER, sphere.uvs, gl2.STATIC_DRAW)
      gl2.enableVertexAttribArray(2)
      gl2.vertexAttribPointer(2, 2, gl2.FLOAT, false, 0, 0)

      // Index buffer
      const idxBuf = gl2.createBuffer()
      gl2.bindBuffer(gl2.ELEMENT_ARRAY_BUFFER, idxBuf)
      gl2.bufferData(gl2.ELEMENT_ARRAY_BUFFER, sphere.indices, gl2.STATIC_DRAW)

      gl2.bindVertexArray(null)

      // --- Initialize Black Marble capture texture (populated by capture layer) ---
      captureTex = gl2.createTexture()
      gl2.bindTexture(gl2.TEXTURE_2D, captureTex)
      gl2.texImage2D(gl2.TEXTURE_2D, 0, gl2.RGBA, 1, 1, 0, gl2.RGBA, gl2.UNSIGNED_BYTE,
        new Uint8Array([0, 0, 0, 255]))
      gl2.texParameteri(gl2.TEXTURE_2D, gl2.TEXTURE_MIN_FILTER, gl2.LINEAR)
      gl2.texParameteri(gl2.TEXTURE_2D, gl2.TEXTURE_MAG_FILTER, gl2.LINEAR)
      gl2.texParameteri(gl2.TEXTURE_2D, gl2.TEXTURE_WRAP_S, gl2.CLAMP_TO_EDGE)
      gl2.texParameteri(gl2.TEXTURE_2D, gl2.TEXTURE_WRAP_T, gl2.CLAMP_TO_EDGE)
      captureW = 0
      captureH = 0

      // --- Initialize Blue-Marble base-colour capture (§7.2 colour correction) ---
      // Filled at the start of every render() so the colour-correction
      // pre-pass can sample the un-corrected base. Same shape as
      // captureTex; resized on first use when the canvas dimensions
      // are known. Starts as a 1×1 black placeholder so any pre-init
      // sample returns a well-defined zero rather than reading
      // uninitialised GPU memory.
      baseTex = gl2.createTexture()
      gl2.bindTexture(gl2.TEXTURE_2D, baseTex)
      gl2.texImage2D(gl2.TEXTURE_2D, 0, gl2.RGBA, 1, 1, 0, gl2.RGBA, gl2.UNSIGNED_BYTE,
        new Uint8Array([0, 0, 0, 255]))
      gl2.texParameteri(gl2.TEXTURE_2D, gl2.TEXTURE_MIN_FILTER, gl2.LINEAR)
      gl2.texParameteri(gl2.TEXTURE_2D, gl2.TEXTURE_MAG_FILTER, gl2.LINEAR)
      gl2.texParameteri(gl2.TEXTURE_2D, gl2.TEXTURE_WRAP_S, gl2.CLAMP_TO_EDGE)
      gl2.texParameteri(gl2.TEXTURE_2D, gl2.TEXTURE_WRAP_T, gl2.CLAMP_TO_EDGE)
      baseW = 0
      baseH = 0

      // --- Load cloud texture asynchronously ---
      cloudTex = gl2.createTexture()
      gl2.bindTexture(gl2.TEXTURE_2D, cloudTex)
      gl2.texImage2D(gl2.TEXTURE_2D, 0, gl2.RGBA, 1, 1, 0, gl2.RGBA, gl2.UNSIGNED_BYTE,
        new Uint8Array([0, 0, 0, 0]))

      const cloudImg = new Image()
      cloudImg.crossOrigin = 'anonymous'
      cloudImg.onload = () => {
        if (disposed) return
        gl2.bindTexture(gl2.TEXTURE_2D, cloudTex)
        // The cloud asset is 10000x5000, which exceeds MAX_TEXTURE_SIZE on
        // some GPUs (notably macOS Firefox, capped at 8192). Downscale via
        // canvas when needed so the upload doesn't silently fail.
        const source = fitImageToMaxTextureSize(gl2, cloudImg)
        gl2.texImage2D(gl2.TEXTURE_2D, 0, gl2.RGBA, gl2.RGBA, gl2.UNSIGNED_BYTE, source)
        gl2.generateMipmap(gl2.TEXTURE_2D)
        gl2.texParameteri(gl2.TEXTURE_2D, gl2.TEXTURE_MIN_FILTER, gl2.LINEAR_MIPMAP_LINEAR)
        gl2.texParameteri(gl2.TEXTURE_2D, gl2.TEXTURE_MAG_FILTER, gl2.LINEAR)
        gl2.texParameteri(gl2.TEXTURE_2D, gl2.TEXTURE_WRAP_S, gl2.REPEAT)
        gl2.texParameteri(gl2.TEXTURE_2D, gl2.TEXTURE_WRAP_T, gl2.CLAMP_TO_EDGE)
        cloudTexReady = true
        checkReady()
        _map.triggerRepaint()
        logger.info('[EarthTileLayer] Cloud texture loaded (%dx%d → %dx%d)',
          cloudImg.width, cloudImg.height, source.width, source.height)
      }
      cloudImg.onerror = () => {
        if (disposed) return
        logger.warn('[EarthTileLayer] Failed to load cloud texture:', CLOUD_TEXTURE_URL)
        cloudTexReady = true
        checkReady()
      }
      cloudImg.src = CLOUD_TEXTURE_URL

      // --- Load specular map texture — deferred to avoid competing with tile bandwidth ---
      specTex = gl2.createTexture()
      gl2.bindTexture(gl2.TEXTURE_2D, specTex)
      gl2.texImage2D(gl2.TEXTURE_2D, 0, gl2.RGBA, 1, 1, 0, gl2.RGBA, gl2.UNSIGNED_BYTE,
        new Uint8Array([0, 0, 0, 255]))

      const loadSpecularMap = () => {
        if (disposed) return
        const specImg = new Image()
        specImg.crossOrigin = 'anonymous'
        specImg.onload = () => {
          if (disposed) return
          gl2.bindTexture(gl2.TEXTURE_2D, specTex)
          gl2.texImage2D(gl2.TEXTURE_2D, 0, gl2.RGBA, gl2.RGBA, gl2.UNSIGNED_BYTE, specImg)
          gl2.generateMipmap(gl2.TEXTURE_2D)
          gl2.texParameteri(gl2.TEXTURE_2D, gl2.TEXTURE_MIN_FILTER, gl2.LINEAR_MIPMAP_LINEAR)
          gl2.texParameteri(gl2.TEXTURE_2D, gl2.TEXTURE_MAG_FILTER, gl2.LINEAR)
          gl2.texParameteri(gl2.TEXTURE_2D, gl2.TEXTURE_WRAP_S, gl2.REPEAT)
          gl2.texParameteri(gl2.TEXTURE_2D, gl2.TEXTURE_WRAP_T, gl2.CLAMP_TO_EDGE)
          specTexReady = true
          checkReady()
          _map.triggerRepaint()
          logger.info('[EarthTileLayer] Specular map loaded (%dx%d)', specImg.width, specImg.height)
        }
        specImg.onerror = () => {
          logger.warn('[EarthTileLayer] Failed to load specular map:', SPECULAR_MAP_URL)
          specTexReady = true
          checkReady()
        }
        specImg.src = SPECULAR_MAP_URL
      }
      typeof requestIdleCallback !== 'undefined'
        ? requestIdleCallback(loadSpecularMap, { timeout: 1000 })
        : setTimeout(loadSpecularMap, 200)

      // --- Load normal-map texture (§7.2) — graceful when missing ---
      // The asset (`earth_normal_8192.jpg`, URL constant above) lives on the same
      // CloudFront-fronted S3 bucket as the VR diffuse / lights
      // tiers; URL above. If the file isn't present we skip the bump
      // pass entirely rather than render a flat-shaded artifact.
      // Deferred via requestIdleCallback so the initial-tile-bandwidth
      // budget isn't shared. Also runs AFTER the specular map's load
      // window so the two large CDN fetches don't race.
      bumpTex = gl2.createTexture()
      gl2.bindTexture(gl2.TEXTURE_2D, bumpTex)
      // Flat-normal placeholder so a sampler bind before the load
      // completes returns a well-defined value. Never actually
      // sampled — the pass is gated on bumpTexReady. RGB8 (not
      // RGBA8) matches the real upload below; the shader only
      // touches `.rgb` so the alpha channel was just costing
      // ~25% extra VRAM (≈32 MB at 8192×4096).
      gl2.texImage2D(gl2.TEXTURE_2D, 0, gl2.RGB8, 1, 1, 0, gl2.RGB, gl2.UNSIGNED_BYTE,
        new Uint8Array([128, 128, 255]))

      // Progressive normal-map tier walker. Mirrors the photorealEarth
      // `loadProgressive` shape: ascending tiers, each successful
      // upload replaces the previous, repaint after every step, halt
      // on the first failure (stale lower tier stays applied). Sequential
      // rather than parallel so a slow 8K fetch doesn't starve the 2K
      // first-bump frame.
      const loadNormalMapTier = (url: string): Promise<boolean> => {
        return new Promise((resolve) => {
          if (disposed) return resolve(false)
          const normalImg = new Image()
          normalImg.crossOrigin = 'anonymous'
          normalImg.onload = () => {
            if (disposed) return resolve(false)
            gl2.bindTexture(gl2.TEXTURE_2D, bumpTex)
            // Sized RGB8 internal format — see the placeholder above.
            gl2.texImage2D(gl2.TEXTURE_2D, 0, gl2.RGB8, gl2.RGB, gl2.UNSIGNED_BYTE, normalImg)
            gl2.generateMipmap(gl2.TEXTURE_2D)
            gl2.texParameteri(gl2.TEXTURE_2D, gl2.TEXTURE_MIN_FILTER, gl2.LINEAR_MIPMAP_LINEAR)
            gl2.texParameteri(gl2.TEXTURE_2D, gl2.TEXTURE_MAG_FILTER, gl2.LINEAR)
            gl2.texParameteri(gl2.TEXTURE_2D, gl2.TEXTURE_WRAP_S, gl2.REPEAT)
            gl2.texParameteri(gl2.TEXTURE_2D, gl2.TEXTURE_WRAP_T, gl2.CLAMP_TO_EDGE)
            bumpTexReady = true
            _map.triggerRepaint()
            logger.info('[EarthTileLayer] Normal map tier loaded (%dx%d)', normalImg.width, normalImg.height)
            resolve(true)
          }
          normalImg.onerror = () => resolve(false)
          normalImg.src = url
        })
      }
      const loadNormalMap = async () => {
        for (const url of NORMAL_MAP_URLS) {
          const ok = await loadNormalMapTier(url)
          if (!ok) {
            // First failure halts progression — whatever tier
            // most recently succeeded stays applied. Logged at
            // info because a missing higher tier is expected
            // (operator may not have uploaded 4K / 8K yet) and
            // a missing 2K just disables bump shading until an
            // asset shows up.
            if (!bumpTexReady) {
              logger.info('[EarthTileLayer] Normal map not available; bump shading disabled')
            } else {
              logger.info('[EarthTileLayer] Normal map progression halted; staying at the last successful tier')
            }
            return
          }
        }
      }
      typeof requestIdleCallback !== 'undefined'
        ? requestIdleCallback(loadNormalMap, { timeout: 1500 })
        : setTimeout(loadNormalMap, 400)

      logger.info('[EarthTileLayer] Day/night+clouds+specular layer initialized (%d triangles)', indexCount / 3)
    },

    render(gl, args) {
      if (!vao) return
      const gl2 = gl as WebGL2RenderingContext
      const matrix = args.defaultProjectionData.mainMatrix

      // Update sun direction every frame (before early returns so skybox layer can use it)
      const sun = sunOverride ?? getSunPosition(new Date())
      sunDir = sunDirectionFromLatLng(sun.lat, sun.lng)

      // Common GL state for sphere passes
      gl2.disable(gl2.DEPTH_TEST)
      gl2.enable(gl2.CULL_FACE)
      gl2.cullFace(gl2.BACK)
      gl2.bindVertexArray(vao)

      // --- Dataset overlay: opaque textured sphere (replaces earth effects) ---
      if (datasetActive && dataset && datasetTex) {
        // For video datasets, re-upload the current frame every render.
        // Also re-upload on forceVideoUpdate (scrubbing while paused).
        if (datasetVideo && datasetVideo.readyState >= 2 && (!datasetVideo.paused || forceVideoUpdate)) {
          gl2.bindTexture(gl2.TEXTURE_2D, datasetTex)
          gl2.texImage2D(gl2.TEXTURE_2D, 0, gl2.RGBA, gl2.RGBA, gl2.UNSIGNED_BYTE, datasetVideo)
          forceVideoUpdate = false
          if (!datasetVideo.paused) mapRef?.triggerRepaint()
        }

        // A data-encoded dataset is the one case with a real alpha
        // source: the palette's own alpha, looked up per fragment
        // from the encoded value. Everything else stays opaque, so
        // the picture path keeps the exact GL state it had before.
        const dataEncoded = Boolean(datasetColorLut)
        if (dataEncoded) {
          gl2.enable(gl2.BLEND)
          // Straight (non-premultiplied) alpha — the LUT is built in
          // straight 8-bit space by `buildColorScaleLut`.
          gl2.blendFuncSeparate(gl2.SRC_ALPHA, gl2.ONE_MINUS_SRC_ALPHA, gl2.ONE, gl2.ONE_MINUS_SRC_ALPHA)
        } else {
          gl2.disable(gl2.BLEND)
        }
        gl2.useProgram(dataset.program)
        gl2.uniformMatrix4fv(dataset.matrixLoc, false, matrix)
        gl2.uniform1f(dataset.radiusScaleLoc, terrainRadiusScale)
        // Phase 3e/B: bbox / lonOrigin / flipY uniforms. Defaults
        // (no bbox, lonOrigin 0, no flip) reduce the shader to the
        // pre-3e equirectangular passthrough, so any dataset
        // without 3d metadata renders bit-identically.
        const bbox = datasetOptions?.boundingBox
        if (bbox) {
          gl2.uniform1i(dataset.hasBboxLoc, 1)
          gl2.uniform4f(dataset.bboxLoc, bbox.n, bbox.s, bbox.w, bbox.e)
        } else {
          gl2.uniform1i(dataset.hasBboxLoc, 0)
          gl2.uniform4f(dataset.bboxLoc, 0, 0, 0, 0)
        }
        gl2.uniform1f(dataset.lonOriginLoc, datasetOptions?.lonOrigin ?? 0)
        gl2.uniform1i(dataset.flipYLoc, datasetOptions?.isFlippedInY ? 1 : 0)
        gl2.activeTexture(gl2.TEXTURE0)
        gl2.bindTexture(gl2.TEXTURE_2D, datasetTex)
        gl2.uniform1i(dataset.texLoc, 0)
        gl2.uniform1i(dataset.dataEncodedLoc, dataEncoded ? 1 : 0)
        if (dataEncoded && datasetColorLut) {
          gl2.activeTexture(gl2.TEXTURE1)
          gl2.bindTexture(gl2.TEXTURE_2D, datasetColorLut)
          gl2.uniform1i(dataset.colorLutLoc, 1)
          gl2.activeTexture(gl2.TEXTURE0)
        }
        // For a picture dataset BLEND is disabled above, so the bbox
        // interior is opaque — the base tile layers (which 3e/C keeps
        // visible for bbox datasets) are revealed solely by the
        // fragment shader's `discard` on the outside-the-box path,
        // never by alpha compositing inside it. A data-encoded
        // dataset is the exception: it enables BLEND and composites
        // inside the box too, because its alpha is a measurement
        // rather than a styling choice.
        gl2.drawElements(gl2.TRIANGLES, indexCount, gl2.UNSIGNED_SHORT, 0)

        // Restore GL state and return — no earth effects when dataset is active
        gl2.bindVertexArray(null)
        gl2.enable(gl2.DEPTH_TEST)
        gl2.disable(gl2.CULL_FACE)
        return
      }

      if (!darken || !visible) {
        gl2.bindVertexArray(null)
        gl2.enable(gl2.DEPTH_TEST)
        gl2.disable(gl2.CULL_FACE)
        return
      }

      // Read the live shader settings snapshot once per frame so
      // every pass below sees a consistent set of values even if a
      // tuner-page slider drag fires mid-frame.
      const shader = getShaderSettings()

      // --- Pass 0: Replace blend — contrast / saturation pre-pass (§7.2) ---
      // Skipped when both uniforms are identity (1.0) — saves the
      // framebuffer copy + a draw call. The threshold is loose
      // (±0.005) so floating-point drift through localStorage /
      // round-trip doesn't accidentally re-enable the pass.
      const ccEnabled = colorCorrect && baseTex && (
        Math.abs(shader.contrast - 1.0) > 0.005 ||
        Math.abs(shader.saturation - 1.0) > 0.005
      )
      if (ccEnabled && colorCorrect && baseTex) {
        const canvas = mapRef?.getCanvas()
        if (canvas) {
          const w = canvas.width, h = canvas.height

          // Copy the current framebuffer (Blue Marble + Black Marble
          // city lights from MapLibre's tile rendering) into baseTex.
          // High texture unit (TEXTURE6) so it doesn't collide with
          // the Black-Marble capture (TEXTURE7) or the per-pass
          // sampler bindings below.
          gl2.activeTexture(gl2.TEXTURE6)
          gl2.bindTexture(gl2.TEXTURE_2D, baseTex)
          if (w !== baseW || h !== baseH) {
            gl2.texImage2D(gl2.TEXTURE_2D, 0, gl2.RGBA, w, h, 0, gl2.RGBA, gl2.UNSIGNED_BYTE, null)
            baseW = w
            baseH = h
          }
          gl2.copyTexSubImage2D(gl2.TEXTURE_2D, 0, 0, 0, 0, 0, w, h)

          // Replace blend — write the corrected colour straight
          // back. The sphere geometry constrains the rewrite to
          // Earth pixels; the skybox / space background outside the
          // sphere is untouched (no fragments shaded there).
          gl2.enable(gl2.BLEND)
          gl2.blendFunc(gl2.ONE, gl2.ZERO)

          gl2.useProgram(colorCorrect.program)
          gl2.uniformMatrix4fv(colorCorrect.matrixLoc, false, matrix)
          gl2.uniform1f(colorCorrect.radiusScaleLoc, terrainRadiusScale)
          gl2.uniform2f(colorCorrect.viewportSizeLoc, w, h)
          gl2.uniform1f(colorCorrect.contrastLoc, shader.contrast)
          gl2.uniform1f(colorCorrect.saturationLoc, shader.saturation)
          gl2.uniform1i(colorCorrect.framebufferLoc, 6)

          gl2.drawElements(gl2.TRIANGLES, indexCount, gl2.UNSIGNED_SHORT, 0)
        }
      }

      // --- Pass 1: Multiply blend — darken night side ---
      gl2.enable(gl2.BLEND)
      gl2.blendFunc(gl2.DST_COLOR, gl2.ZERO) // result = src * dst

      gl2.useProgram(darken.program)
      gl2.uniformMatrix4fv(darken.matrixLoc, false, matrix)
      gl2.uniform1f(darken.radiusScaleLoc, terrainRadiusScale)
      gl2.uniform3f(darken.sunDirLoc, sunDir[0], sunDir[1], sunDir[2])
      gl2.drawElements(gl2.TRIANGLES, indexCount, gl2.UNSIGNED_SHORT, 0)

      // --- Pass 1.5: Multiply blend — normal-map bump shading (§7.2) ---
      // Day-side only; gated on the bumpStrength uniform AND the
      // normal-map asset being present. Skipped when strength is
      // effectively zero so a "no bump" tuner setting saves the
      // draw call.
      if (bump && bumpTexReady && shader.bumpStrength > 0.005) {
        gl2.blendFunc(gl2.DST_COLOR, gl2.ZERO) // multiply

        gl2.useProgram(bump.program)
        gl2.uniformMatrix4fv(bump.matrixLoc, false, matrix)
        gl2.uniform1f(bump.radiusScaleLoc, terrainRadiusScale)
        gl2.uniform3f(bump.sunDirLoc, sunDir[0], sunDir[1], sunDir[2])
        gl2.uniform1f(bump.strengthLoc, shader.bumpStrength)

        gl2.activeTexture(gl2.TEXTURE0)
        gl2.bindTexture(gl2.TEXTURE_2D, bumpTex)
        gl2.uniform1i(bump.normalMapLoc, 0)

        gl2.drawElements(gl2.TRIANGLES, indexCount, gl2.UNSIGNED_SHORT, 0)
      }

      // --- Pass 2: Additive blend — overlay Black Marble city lights (screen-space) ---
      if (lights && captureTex && captureW > 0) {
        gl2.blendFunc(gl2.ONE, gl2.ONE) // result = src + dst

        gl2.useProgram(lights.program)
        gl2.uniformMatrix4fv(lights.matrixLoc, false, matrix)
        gl2.uniform1f(lights.radiusScaleLoc, terrainRadiusScale)
        gl2.uniform3f(lights.sunDirLoc, sunDir[0], sunDir[1], sunDir[2])
        gl2.uniform1f(lights.strengthLoc, NIGHT_LIGHT_STRENGTH)
        gl2.uniform2f(lights.viewportSizeLoc, captureW, captureH)

        gl2.activeTexture(gl2.TEXTURE0)
        gl2.bindTexture(gl2.TEXTURE_2D, captureTex)
        gl2.uniform1i(lights.blackMarbleLoc, 0)

        gl2.drawElements(gl2.TRIANGLES, indexCount, gl2.UNSIGNED_SHORT, 0)
      }

      // Cloud zoom-fade: fully visible at zoom ≤3, fully gone at
      // zoom ≥6. Both the cloud pass (pass 4) and the specular
      // pass's cloud-mask gating (pass 3) use the same value so the
      // glint-suppression mask vanishes alongside the clouds it
      // models — otherwise high zoom shows cloud-shaped patches of
      // missing glint over water.
      const zoom = mapRef?.getZoom() ?? 0
      const cloudZoomFade = 1.0 - Math.max(0, Math.min(1, (zoom - 3) / 3))

      // --- Pass 3: Additive blend — specular sun glint on water ---
      if (specular && specTexReady && mapRef) {
        gl2.blendFunc(gl2.ONE, gl2.ONE) // additive

        // View direction: approximate as ECEF normal at camera center
        const center = mapRef.getCenter()
        const vLatR = center.lat * Math.PI / 180
        const vLngR = center.lng * Math.PI / 180
        const viewDir: [number, number, number] = [
          Math.cos(vLatR) * Math.sin(vLngR),
          Math.sin(vLatR),
          Math.cos(vLatR) * Math.cos(vLngR),
        ]

        gl2.useProgram(specular.program)
        gl2.uniformMatrix4fv(specular.matrixLoc, false, matrix)
        gl2.uniform1f(specular.radiusScaleLoc, terrainRadiusScale)
        gl2.uniform3f(specular.sunDirLoc, sunDir[0], sunDir[1], sunDir[2])
        gl2.uniform3f(specular.viewDirLoc, viewDir[0], viewDir[1], viewDir[2])
        gl2.uniform1f(specular.shininessLoc, SPECULAR_SHININESS)
        // Strength is user-controllable via the Tools menu specular
        // preset (None / Default / Comfortable, §7.2). The `shader`
        // snapshot was taken once at the top of this render() so
        // every pass sees a consistent set of values.
        gl2.uniform1f(specular.strengthLoc, shader.specularStrength)

        gl2.activeTexture(gl2.TEXTURE0)
        gl2.bindTexture(gl2.TEXTURE_2D, specTex)
        gl2.uniform1i(specular.specMapLoc, 0)

        gl2.activeTexture(gl2.TEXTURE1)
        gl2.bindTexture(gl2.TEXTURE_2D, cloudTex)
        gl2.uniform1i(specular.cloudMaskLoc, 1)
        gl2.uniform1f(specular.cloudAlphaGammaLoc, CLOUD_ALPHA_GAMMA)
        gl2.uniform1f(specular.cloudZoomFadeLoc, cloudZoomFade)

        gl2.drawElements(gl2.TRIANGLES, indexCount, gl2.UNSIGNED_SHORT, 0)
      }

      // --- Pass 4: Alpha blend — clouds with day/night darkening ---
      if (clouds && cloudTexReady) {
        // Standard alpha blend: clouds over existing content
        gl2.blendFunc(gl2.SRC_ALPHA, gl2.ONE_MINUS_SRC_ALPHA)

        gl2.useProgram(clouds.program)
        gl2.uniformMatrix4fv(clouds.matrixLoc, false, matrix)
        gl2.uniform3f(clouds.sunDirLoc, sunDir[0], sunDir[1], sunDir[2])
        gl2.uniform1f(clouds.radiusLoc, CLOUD_RADIUS * terrainRadiusScale)
        gl2.uniform1f(clouds.opacityLoc, CLOUD_OPACITY)
        gl2.uniform1f(clouds.alphaGammaLoc, CLOUD_ALPHA_GAMMA)
        gl2.uniform1f(clouds.nightDarkeningLoc, CLOUD_NIGHT_DARKENING)
        gl2.uniform1f(clouds.zoomFadeLoc, cloudZoomFade)

        gl2.activeTexture(gl2.TEXTURE0)
        gl2.bindTexture(gl2.TEXTURE_2D, cloudTex)
        gl2.uniform1i(clouds.cloudTexLoc, 0)

        gl2.drawElements(gl2.TRIANGLES, indexCount, gl2.UNSIGNED_SHORT, 0)
      }

      // --- Pass 5: Additive blend — atmospheric scattering halo ---
      // Rendered last so the limb glow sits in front of clouds,
      // matching the article's render order and photorealEarth.ts.
      // Tier-2 bounded raymarch: the shell mesh is the atmosphere
      // ceiling; each front-face fragment is the camera-side entry
      // point of that pixel's ray, and the shader integrates inward
      // through Rayleigh + Mie + ozone densities.
      // Camera position derived from the projection matrix so the
      // raymarch ray geometry is correct at any zoom level. ECEF
      // unit-sphere coords scaled to km for the shader. Skip the
      // atmosphere pass entirely on failure rather than placing the
      // camera at (0, 0, 0) — the raymarch would treat that as the
      // planet centre and produce extreme artifacts.
      if (
        atmosphere && mapRef &&
        computeCameraWorldPosFromMatrix(matrix, cameraEcefScratch)
      ) {
        // Article-style composition (Tier 3.5 fix):
        //   result = src.rgb × 1 + dst.rgb × src.alpha
        //         = scattered + bg × viewTransmittance
        // The fragment writes ACES-tonemapped scattered light in
        // RGB and the scalar view transmittance in alpha. With this
        // blend func the framebuffer is BOTH brightened by the
        // scattered light AND dimmed by the path-length-dependent
        // viewTrans — without the dimming term the atmosphere pass
        // washed out the planet face at noon viewing.
        gl2.blendFunc(gl2.ONE, gl2.SRC_ALPHA)

        const cx = cameraEcefScratch[0] * PLANET_RADIUS_KM
        const cy = cameraEcefScratch[1] * PLANET_RADIUS_KM
        const cz = cameraEcefScratch[2] * PLANET_RADIUS_KM

        // Cull selection. The shell mesh has outward-facing normals,
        // so from OUTSIDE the shell the front (camera-facing) side
        // is what we want — default BACK culling. From INSIDE the
        // shell (high zoom, camera within the atmosphere), front
        // faces point away from the camera and BACK culling would
        // hide the atmosphere entirely; switching to FRONT culling
        // renders the inner surface that's actually visible.
        const shellRadiusEcef = terrainRadiusScale * ATMOSPHERE_RADIUS_FACTOR
        const cameraDistEcefSq =
          cameraEcefScratch[0] * cameraEcefScratch[0] +
          cameraEcefScratch[1] * cameraEcefScratch[1] +
          cameraEcefScratch[2] * cameraEcefScratch[2]
        const cameraInsideShell = cameraDistEcefSq < shellRadiusEcef * shellRadiusEcef
        gl2.cullFace(cameraInsideShell ? gl2.FRONT : gl2.BACK)

        gl2.useProgram(atmosphere.program)
        gl2.uniformMatrix4fv(atmosphere.matrixLoc, false, matrix)
        gl2.uniform1f(atmosphere.radiusScaleLoc, terrainRadiusScale * ATMOSPHERE_RADIUS_FACTOR)
        gl2.uniform3f(atmosphere.sunDirLoc, sunDir[0], sunDir[1], sunDir[2])
        gl2.uniform3f(atmosphere.cameraPosKmLoc, cx, cy, cz)
        gl2.uniform1f(atmosphere.intensityLoc, ATMOSPHERE_INTENSITY)

        gl2.activeTexture(gl2.TEXTURE0)
        gl2.bindTexture(gl2.TEXTURE_2D, transmittanceLutTex)
        gl2.uniform1i(atmosphere.transmittanceLutLoc, 0)

        gl2.drawElements(gl2.TRIANGLES, indexCount, gl2.UNSIGNED_SHORT, 0)
      }

      // Restore MapLibre's expected GL state
      gl2.bindVertexArray(null)
      gl2.disable(gl2.BLEND)
      gl2.enable(gl2.DEPTH_TEST)
      gl2.disable(gl2.CULL_FACE)
    },

    onRemove(_map: MaplibreMap, gl: WebGL2RenderingContext | WebGLRenderingContext) {
      disposed = true
      unsubscribeShaderSettings?.()
      unsubscribeShaderSettings = null
      const gl2 = gl as WebGL2RenderingContext
      if (dataset) gl2.deleteProgram(dataset.program)
      if (darken) gl2.deleteProgram(darken.program)
      if (lights) gl2.deleteProgram(lights.program)
      if (specular) gl2.deleteProgram(specular.program)
      if (clouds) gl2.deleteProgram(clouds.program)
      if (atmosphere) gl2.deleteProgram(atmosphere.program)
      if (colorCorrect) gl2.deleteProgram(colorCorrect.program)
      if (bump) gl2.deleteProgram(bump.program)
      if (vao) gl2.deleteVertexArray(vao)
      if (captureTex) gl2.deleteTexture(captureTex)
      if (baseTex) gl2.deleteTexture(baseTex)
      if (bumpTex) gl2.deleteTexture(bumpTex)
      if (specTex) gl2.deleteTexture(specTex)
      if (cloudTex) gl2.deleteTexture(cloudTex)
      if (datasetTex) gl2.deleteTexture(datasetTex)
      if (transmittanceLutTex) gl2.deleteTexture(transmittanceLutTex)
    },
  }

  // Separate skybox layer — renders in '3d' mode (after all 2D layers)
  // so the depth buffer contains the globe and stars only draw in space.
  const skyboxLayerDef: CustomLayerInterface = {
    id: 'earth-skybox-layer',
    type: 'custom',
    renderingMode: '3d',

    onAdd() { /* resources shared with earth-tile-layer via closure */ },

    render(gl, args) {
      if (!skyboxReady || !skyboxProg || !mapRef) return
      const gl2 = gl as WebGL2RenderingContext

      gl2.enable(gl2.DEPTH_TEST)
      gl2.depthFunc(gl2.LEQUAL)
      gl2.depthMask(false)
      gl2.disable(gl2.BLEND)
      gl2.disable(gl2.CULL_FACE)

      gl2.useProgram(skyboxProg)
      gl2.uniform1f(skyboxBrightnessLoc, STAR_BRIGHTNESS)

      const canvas = mapRef.getCanvas()
      const aspect = canvas.width / canvas.height
      const fov = ((mapRef as any).transform?._fov ?? 0.6435) as number
      gl2.uniform2f(skyboxAspectFovLoc, aspect, fov)

      const center = mapRef.getCenter()
      const bearing = mapRef.getBearing() * Math.PI / 180
      const pitch = mapRef.getPitch() * Math.PI / 180
      const lat = center.lat * Math.PI / 180
      const lng = center.lng * Math.PI / 180
      gl2.uniform4f(skyboxCameraLoc, lat, lng, bearing, pitch)

      for (let i = 0; i < 6; i++) {
        gl2.activeTexture(gl2.TEXTURE0 + i)
        gl2.bindTexture(gl2.TEXTURE_2D, skyboxFaceTextures[i])
        gl2.uniform1i(skyboxFaceLocs[i], i)
      }

      gl2.bindVertexArray(null)
      gl2.drawArrays(gl2.TRIANGLES, 0, 3)

      gl2.depthMask(true)
      gl2.depthFunc(gl2.LESS)

      // --- Sun sprite: billboard at the sun's projected screen position ---
      // Depth occlusion uses the same technique as the skybox: render at depth 1.0
      // with depthFunc(LEQUAL). The globe writes depth < 1.0, so fragments over
      // the globe fail. Empty space has depth = 1.0, so 1.0 <= 1.0 passes.
      if (sunProg && mapRef) {
        const sx = sunDir[0] * SUN_WORLD_DISTANCE
        const sy = sunDir[1] * SUN_WORLD_DISTANCE
        const sz = sunDir[2] * SUN_WORLD_DISTANCE

        const m = args.defaultProjectionData.mainMatrix
        const clipX = m[0]*sx + m[4]*sy + m[8]*sz + m[12]
        const clipY = m[1]*sx + m[5]*sy + m[9]*sz + m[13]
        const clipW = m[3]*sx + m[7]*sy + m[11]*sz + m[15]

        if (clipW > 0) {
          const canvas = mapRef.getCanvas()
          const aspect = canvas.width / canvas.height

          gl2.enable(gl2.BLEND)
          gl2.blendFunc(gl2.SRC_ALPHA, gl2.ONE)
          gl2.enable(gl2.DEPTH_TEST)
          gl2.depthFunc(gl2.LEQUAL)
          gl2.depthMask(false)

          gl2.useProgram(sunProg.program)
          gl2.uniform4f(sunProg.sunClipLoc, clipX, clipY, clipW, clipW) // z=w so ndcZ=1.0
          gl2.uniform1f(sunProg.scaleLoc, SUN_SCREEN_SCALE)
          gl2.uniform1f(sunProg.aspectLoc, aspect)
          gl2.uniform1f(sunProg.coreRadiusLoc, SUN_CORE_RADIUS)
          gl2.uniform1f(sunProg.glowFalloffLoc, SUN_GLOW_FALLOFF)
          gl2.uniform3f(sunProg.coreColorLoc, ...SUN_CORE_COLOR)
          gl2.uniform3f(sunProg.glowColorLoc, ...SUN_GLOW_COLOR)

          gl2.bindVertexArray(null)
          gl2.drawArrays(gl2.TRIANGLES, 0, 6)

          gl2.disable(gl2.BLEND)
          gl2.depthMask(true)
        }
      }

      gl2.enable(gl2.DEPTH_TEST)
      gl2.disable(gl2.CULL_FACE)
    },

    onRemove(_map: MaplibreMap, gl: WebGL2RenderingContext | WebGLRenderingContext) {
      const gl2 = gl as WebGL2RenderingContext
      if (skyboxProg) gl2.deleteProgram(skyboxProg)
      if (sunProg) gl2.deleteProgram(sunProg.program)
      for (const tex of skyboxFaceTextures) if (tex) gl2.deleteTexture(tex)
    },
  }

  // Capture layer — renders between Black Marble and Blue Marble in the
  // MapLibre layer stack. Copies the framebuffer (containing rendered Black
  // Marble tiles) to captureTex for use in the lights pass.
  const captureLayerDef: CustomLayerInterface = {
    id: 'black-marble-capture',
    type: 'custom',
    renderingMode: '2d',

    onAdd() { /* resources shared with earth-tile-layer via closure */ },

    render(gl) {
      // Skip capture when dataset overlay is active (no night lights needed)
      if (datasetActive || !visible) return
      if (!captureTex) return

      const gl2 = gl as WebGL2RenderingContext
      const canvas = mapRef?.getCanvas()
      if (!canvas) return
      const w = canvas.width, h = canvas.height

      gl2.activeTexture(gl2.TEXTURE7) // high unit to avoid MapLibre conflicts
      gl2.bindTexture(gl2.TEXTURE_2D, captureTex)

      // Resize capture texture if canvas dimensions changed
      if (w !== captureW || h !== captureH) {
        gl2.texImage2D(gl2.TEXTURE_2D, 0, gl2.RGBA, w, h, 0, gl2.RGBA, gl2.UNSIGNED_BYTE, null)
        captureW = w
        captureH = h
      }

      // Copy current framebuffer (Black Marble tiles) to texture
      gl2.copyTexSubImage2D(gl2.TEXTURE_2D, 0, 0, 0, 0, 0, w, h)
    },

    onRemove() { /* cleanup handled by earth-tile-layer */ },
  }

  return {
    layer,
    captureLayer: captureLayerDef,
    skyboxLayer: skyboxLayerDef,
    ready: readyPromise,
    setSunPosition(lat: number, lng: number) {
      sunOverride = { lat, lng }
      if (mapRef) {
        syncAtmosphereLight(mapRef, lat, lng)
        mapRef.triggerRepaint()
      }
    },
    clearSunOverride() {
      sunOverride = null
      if (mapRef) {
        const sun = getSunPosition(new Date())
        syncAtmosphereLight(mapRef, sun.lat, sun.lng)
        mapRef.triggerRepaint()
      }
    },
    setVisible(v: boolean) {
      visible = v
      mapRef?.triggerRepaint()
    },
    setTerrainExaggeration(exaggeration: number) {
      // Match MapLibre's globe terrain formula: displacement = exag * elevation / GLOBE_RADIUS
      // where GLOBE_RADIUS = 6_371_008.8 (from maplibre-gl/src/geo/lng_lat.ts).
      // Max elevation ~8848m (Everest). Add 5% margin to prevent z-fighting.
      const MAX_ELEVATION_M = 8848
      const GLOBE_RADIUS = 6_371_008.8
      terrainRadiusScale = 1.0 + (MAX_ELEVATION_M * exaggeration / GLOBE_RADIUS) * 1.05
      mapRef?.triggerRepaint()
    },
    setDatasetTexture(
      image: HTMLCanvasElement | HTMLImageElement,
      options?: DatasetOverlayOptions,
    ) {
      if (!glRef) return
      datasetVideo = null // clear any previous video
      if (!datasetTex) {
        datasetTex = glRef.createTexture()
      }
      glRef.bindTexture(glRef.TEXTURE_2D, datasetTex)
      const source = fitImageToMaxTextureSize(glRef, image)
      glRef.texImage2D(glRef.TEXTURE_2D, 0, glRef.RGBA, glRef.RGBA, glRef.UNSIGNED_BYTE, source)
      datasetColorLut = syncColorLut(glRef, datasetColorLut, options, datasetDisplay)
      if (datasetColorLut) {
        applyDataEncodedFiltering(glRef)
      } else {
        glRef.generateMipmap(glRef.TEXTURE_2D)
        glRef.texParameteri(glRef.TEXTURE_2D, glRef.TEXTURE_MIN_FILTER, glRef.LINEAR_MIPMAP_LINEAR)
        glRef.texParameteri(glRef.TEXTURE_2D, glRef.TEXTURE_MAG_FILTER, glRef.LINEAR)
      }
      glRef.texParameteri(glRef.TEXTURE_2D, glRef.TEXTURE_WRAP_S, glRef.REPEAT)
      glRef.texParameteri(glRef.TEXTURE_2D, glRef.TEXTURE_WRAP_T, glRef.CLAMP_TO_EDGE)
      datasetActive = true
      // `?? null` (not `?? datasetOptions`) so back-to-back swaps
      // without a `clearDatasetTexture()` in between don't leak the
      // prior dataset's bbox / lonOrigin / flipY onto the new one.
      // Callers should pass `options` whenever the new dataset has
      // 3d metadata; omit only for the legacy fast-path case.
      datasetOptions = options ?? null
      mapRef?.triggerRepaint()
    },
    setDatasetVideo(video: HTMLVideoElement, options?: DatasetOverlayOptions) {
      if (!glRef) return
      datasetVideo = video
      if (!datasetTex) {
        datasetTex = glRef.createTexture()
      }
      // Initialize with a single frame — render loop will update per-frame
      glRef.bindTexture(glRef.TEXTURE_2D, datasetTex)
      glRef.texImage2D(glRef.TEXTURE_2D, 0, glRef.RGBA, glRef.RGBA, glRef.UNSIGNED_BYTE, video)
      datasetColorLut = syncColorLut(glRef, datasetColorLut, options, datasetDisplay)
      if (datasetColorLut) {
        applyDataEncodedFiltering(glRef)
      } else {
        // No mipmaps for video — LINEAR only (regenerating mipmaps per frame is expensive)
        glRef.texParameteri(glRef.TEXTURE_2D, glRef.TEXTURE_MIN_FILTER, glRef.LINEAR)
        glRef.texParameteri(glRef.TEXTURE_2D, glRef.TEXTURE_MAG_FILTER, glRef.LINEAR)
      }
      glRef.texParameteri(glRef.TEXTURE_2D, glRef.TEXTURE_WRAP_S, glRef.REPEAT)
      glRef.texParameteri(glRef.TEXTURE_2D, glRef.TEXTURE_WRAP_T, glRef.CLAMP_TO_EDGE)
      datasetActive = true
      datasetOptions = options ?? null
      mapRef?.triggerRepaint()
    },
    requestVideoUpdate() {
      if (datasetVideo) {
        forceVideoUpdate = true
        mapRef?.triggerRepaint()
      }
    },
    setColorScaleDisplay(display: ColorScaleDisplay) {
      datasetDisplay = display
      // Only the LUT is rebuilt. The dataset texture is not re-uploaded
      // and no pixels are read back, which is what makes this usable on
      // a slider while a video is playing.
      if (!glRef || !datasetColorLut) return
      datasetColorLut = syncColorLut(glRef, datasetColorLut, datasetOptions ?? undefined, display)
      mapRef?.triggerRepaint()
    },
    clearDatasetTexture() {
      datasetActive = false
      datasetVideo = null
      datasetOptions = null
      // Drop the palette with the dataset, so the next one can't
      // inherit it and render its values through a stranger's ramp.
      if (datasetColorLut && glRef) glRef.deleteTexture(datasetColorLut)
      datasetColorLut = null
      mapRef?.triggerRepaint()
    },
  }
}
